import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLayout } from '../../../context/LayoutContext';
import {
  Box, Typography, Grid, Paper, Select, MenuItem, FormControl, InputLabel,
  IconButton, Card, CardContent, CircularProgress, Alert, Button, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, TextField,
  Tooltip, Divider, ToggleButtonGroup, ToggleButton, FormControlLabel, Switch,
  Breadcrumbs, Link, Checkbox, Tabs, Tab, RadioGroup, Radio, useTheme, useMediaQuery
} from '@mui/material';
import {
  ArrowBackIos, ArrowForwardIos, Today, Email, Edit, Delete, DesktopWindows,
  Language, Send, NavigateNext, Print, EventAvailable, FilterAlt, Clear,
  CalendarMonth, People, BusinessCenter, ViewList, GridView
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  useGetWeeklyShiftsByWardQuery, useAssignStaffToShiftMutation, useUpdateShiftMutation,
  useReOpenShiftMutation, useCreateShiftMutation, useNotifyShiftCancellationMutation
} from '../../../slices/shiftApiSlice';
import { useGetUsersQuery } from '../../../slices/usersApiSlice';
import { useSelector } from 'react-redux';
// Agency API
import { useGetAgenciesQuery, useAssignAgencyToShiftMutation } from '../../../slices/agencyApiSlice';
// Availability API
import { useGetWeeklyAvailabilitiesByStaffQuery } from '../../../slices/availabilityApiSlice';
// Hospital locations
import { useGetHospitalLocationsQuery } from '../../../slices/hospitalLocationApiSlice';
// Enums
import { CATEGORY_OF_STAFF, REASON_FOR_REQUEST, SHIFT_STATUS, LINE_STATUS, SPECIALS_REASON } from '../../../utils/enums';
import { useLocation } from 'react-router-dom';
import { dublinLocalToUtcISO, dublinTimeStr, dublinDateStr } from '../../../utils/dublinTime';

// Shift presets
const SHIFT_PRESETS = {
  'LD': { start: '08:00', end: '20:00', label: 'Long Day (8:00-20:00)' },
  'ND': { start: '20:00', end: '08:00', label: 'Night Duty (20:00-8:00)' },
  'AM': { start: '08:00', end: '16:00', label: 'Morning (8:00-16:00)' },
  'PM': { start: '14:00', end: '20:00', label: 'Afternoon (14:00-20:00)' },
  'TW': { start: '20:00', end: '00:00', label: 'Twilight (20:00-00:00)' },
  'ELD': { start: '07:30', end: '20:30', label: 'Extended Long Day (7:30-20:30)' },
  'ESCORT': { start: '08:00', end: '20:00', label: 'Escort (8:00-20:00)' },
};

const EMPTY_LOCATIONS = [];

// Tier color mapping for agencies
const TIER_COLORS = {
  'Tier1': '#4caf50',
  'Tier2': '#2196f3',
  'Tier3': '#ff9800',
  'Tier4': '#f44336',
};

const UNAVAILABILITY_REASONS = [
  'Annual Leave', 'Adoptive Leave', 'Bereavement Leave', 'Carers Leave.',
  'Certified Sick Leave', 'Force Majeure', 'Health and Safety Leave', 'Jury Leave',
  'Maternity Leave', 'Other', 'Parents Leave', 'Parental Leave', 'Paternity Leave',
  'Sick Leave', 'Study Leave / Mandatory Training',
];

// Job titles that should only be visible to admins (e.g. "ECT Nurse", "Bank HCA")
const isAdminOnlyJobTitle = (jobtitle) => {
  if (!jobtitle) return false;
  const trimmed = jobtitle.trim().toLowerCase();
  return trimmed.startsWith('ect') || trimmed.startsWith('bank');
};

const scrollableTableStyle = {
  maxHeight: '70vh',
  overflowY: 'auto',
  overflowX: 'auto',
  border: '1px solid #ddd',
  borderRadius: '4px',
  position: 'relative',
  WebkitOverflowScrolling: 'touch',
  width: '100%',
};

const fixedHeaderStyle = {
  position: 'sticky',
  top: 0,
  zIndex: 10,
  backgroundColor: 'white',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  display: 'flex',
  width: '100%',
};

const globalStyles = `@media (max-width: 600px) {
  .ward-row { font-size: 12px; }
  .MuiCardContent-root { padding: 4px !important; }
  .MuiTypography-root { font-size: 0.7rem !important; }
}`;

// Print styles
const PrintStyles = () => (
  <style>{`
    @media print {
      .no-print { display: none !important; }
      .availability-section { display: none !important; }
      .print-page { page-break-inside: avoid !important; break-inside: avoid !important; }
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
      @-moz-document url-prefix() {
        .shift-card, .ward-card, .staff-card, .header-cell {
          box-shadow: inset 0 0 0 1000px var(--print-color) !important;
        }
      }
      body { margin: 0 !important; padding: 0 !important; background: white !important; }
      .print-container { width: 100% !important; margin: 0 !important; padding: 0 !important; }
      .overtime-badge {
        background-color: #ff9800 !important; color: white !important; font-weight: bold;
        padding: 2px 4px; border-radius: 4px; font-size: 8px; display: inline-block; margin-top: 2px;
      }
      .calendar-table { width: 100% !important; table-layout: fixed !important; border-collapse: collapse !important; }
      .shift-text, .ward-text { color: #000 !important; font-weight: bold !important; }
      .print-text { font-size: 11pt !important; line-height: 1.4 !important; }
      @page { margin: 0.5in !important; }
      .ward-row { page-break-inside: avoid !important; break-inside: avoid !important; }
      .print-header {
        text-align: center !important; margin-bottom: 20px !important;
        border-bottom: 2px solid #000 !important; padding-bottom: 10px !important;
      }
      .shift-assigned { background-color: #4caf50 !important; color: white !important; }
      .shift-agency-assigned { background-color: #9c27b0 !important; color: white !important; }
      .shift-cancelled { background-color: #fbc02d !important; border: 1px solid #f9a825; color: black !important; }
      .shift-weekend-assigned { background-color: #ff9800 !important; color: white !important; }
      .shift-available { background-color: #2196f3 !important; color: white !important; }
      .shift-weekend-available { background-color: #ffb74d !important; color: black !important; }
      .ward-even { background-color: #f9f9f9 !important; }
      .ward-odd { background-color: white !important; }
      .ward-weekend { background-color: rgba(255, 152, 0, 0.05) !important; }
    }
  `}</style>
);

const WardWeeklyView = () => {
  const { setFluid } = useLayout();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedWeek, setSelectedWeek] = useState(new Date());
  const [selectedWardFilter, setSelectedWardFilter] = useState('all');
  const [selectedJobTitleFilter, setSelectedJobTitleFilter] = useState('all');
  const [selectedDayFilter, setSelectedDayFilter] = useState('all');
  const [wards, setWards] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [filteredStaffList, setFilteredStaffList] = useState([]);
  const [wardShifts, setWardShifts] = useState({});
  const [draggedStaff, setDraggedStaff] = useState(null);
  const [shiftToEdit, setShiftToEdit] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedWardForEmail, setSelectedWardForEmail] = useState(null);
  const [emailContent, setEmailContent] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMethod, setEmailMethod] = useState('outlook');
  const [includeWeekend, setIncludeWeekend] = useState(true);
  const [staffSearchQuery, setStaffSearchQuery] = useState('');
  const [advanceNoticeGiven, setAdvanceNoticeGiven] = useState(null);

  // Staff/agency source toggle
  const [staffSource, setStaffSource] = useState('internal');

  // Agencies
  const [agencies, setAgencies] = useState([]);
  const [filteredAgenciesList, setFilteredAgenciesList] = useState([]);
  const [draggedAgency, setDraggedAgency] = useState(null);

  // Agency worker details dialog
  const [agencyWorkerDialogOpen, setAgencyWorkerDialogOpen] = useState(false);
  const [selectedShiftForAgency, setSelectedShiftForAgency] = useState(null);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [agencyWorkerDetails, setAgencyWorkerDetails] = useState({
    workerName: '', workerContact: '', workerEmail: '',
  });

  // Shift release/unassign
  const [shiftToRelease, setShiftToRelease] = useState(null);
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  // Create new shift
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newShiftInfo, setNewShiftInfo] = useState({ ward: '', dayDate: '', dayInfo: null });

  const location = useLocation();
  const [selectedReasonFilter, setSelectedReasonFilter] = useState('all');

  // Edit dialog tabs
  const [activeTab, setActiveTab] = useState('basic');

  // Mobile: which panel is showing ('shifts' | 'staff')
  const [mobileView, setMobileView] = useState('shifts');

  // Enhanced edit form state
  const [editForm, setEditForm] = useState({
    startDate: '', startTime: '', endDate: '', endTime: '', selectedPreset: '',
    hospitalLocationId: '', wardDepartmentArea: '', categoryOfStaff: '',
    reasonForRequest: '', reasonForRequestDetails: '', escort: false, special: false,
    isOvertime: false, isActive: true, eccNumber: '', specialRequirements: '',
    comments: '', workerName: '', workerContact: '', workerEmail: '', specialsReason: '',
  });

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const isShiftApproved = (shift) => shift?.actualStatus === 'Complete';

  // A shift is "pending signoff" once its end time has passed but nobody has yet
  // recorded what actually happened (actualStatus is still neither 'Complete' nor
  // 'Not-Worked'). Only meaningful for shifts that were actually filled — an
  // available/unfilled shift has nothing for a manager to sign off on — and not
  // for agency shifts already marked cancelled, which are done via a separate flow.
  const isShiftPendingApproval = (shift) => {
    if (!shift?.isFilled || shift.agencyCancelled) return false;
    const endDateTime = shift?.shiftData?.endDateTime;
    if (!endDateTime) return false;
    if (new Date(endDateTime) > new Date()) return false;
    return shift.actualStatus !== 'Complete' && shift.actualStatus !== 'Not-Worked';
  };

  const toLocalDateKey = (dateTimeStr) => {
    const d = new Date(dateTimeStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const isEveningShift = (startTime) => {
    if (!startTime) return false;
    const [hours] = startTime.split(':').map(Number);
    return hours >= 18;
  };

  const { userInfo } = useSelector((state) => state.auth);

  const [createShift] = useCreateShiftMutation();
  const [assignAgencyToShift] = useAssignAgencyToShiftMutation();

  const hasSetFluid = useRef(false);

  // Get week dates
  const getWeekDates = (date) => {
    const current = new Date(date);
    current.setHours(0, 0, 0, 0);
    const day = current.getDay();
    const diff = current.getDate() - day + (day === 0 ? -6 : 1);

    const monday = new Date(current);
    monday.setDate(diff);
    monday.setHours(0, 0, 0, 0);

    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);

    const toLocalISO = (d) => {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };

    return {
      startDate: toLocalISO(monday),
      endDate: toLocalISO(sunday),
      fullEndDate: sunday.toISOString(),
      days: Array.from({ length: 7 }, (_, i) => {
        const d = new Date(monday);
        d.setDate(monday.getDate() + i);
        const dayOfWeek = d.getDay();
        return {
          date: toLocalISO(d),
          dayName: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayOfWeek],
          shortName: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dayOfWeek],
          isWeekend: dayOfWeek === 0 || dayOfWeek === 6
        };
      })
    };
  };

  const weekInfo = useMemo(() => getWeekDates(selectedWeek), [selectedWeek]);

  // ============= MANAGER DIRECTORATE FILTERING =============
  const managerDirectorate = userInfo?.directorateName || null;
  const isManager = userInfo?.isManager === true;
  const isAdmin = userInfo?.isAdmin === true;

  const { data: hospitalLocations = EMPTY_LOCATIONS, isLoading: loadingLocations } = useGetHospitalLocationsQuery();

  const filteredHospitalLocations = useMemo(() => {
    if (!hospitalLocations || hospitalLocations.length === 0) return [];
    if (isAdmin) return hospitalLocations;
    if (isManager && managerDirectorate) {
      return hospitalLocations.filter(location => location.directorate === managerDirectorate);
    }
    return hospitalLocations;
  }, [hospitalLocations, isAdmin, isManager, managerDirectorate]);

  const filteredWardNames = useMemo(() => {
    return filteredHospitalLocations.map(location => location.wardDepartmentArea).filter(Boolean);
  }, [filteredHospitalLocations]);
  // ============= END MANAGER DIRECTORATE FILTERING =============

  const accessType = useMemo(() => {
    if (userInfo?.isAdmin) return 'Admin';
    if (userInfo?.isManager) return 'Manager';
    return 'User';
  }, [userInfo]);

  const { data: shiftsData, isLoading: loadingShifts, refetch: refetchShifts } = useGetWeeklyShiftsByWardQuery({
    startDate: weekInfo.startDate,
    endDate: weekInfo.fullEndDate,
    ward: selectedWardFilter === 'all' ? '' : selectedWardFilter,
    accessType
  }, { skip: !weekInfo.startDate });

  const { data: staffData, isLoading: loadingStaff } = useGetUsersQuery();

  const { data: agenciesData, isLoading: loadingAgencies } = useGetAgenciesQuery(
    { isActive: true },
    { skip: !weekInfo.startDate }
  );

  const { data: availabilitiesData, isLoading: loadingAvailabilities } = useGetWeeklyAvailabilitiesByStaffQuery({
    startDate: weekInfo.startDate,
    endDate: weekInfo.fullEndDate
  }, { skip: !weekInfo.startDate });

  const [assignStaff] = useAssignStaffToShiftMutation();
  const [updateShift] = useUpdateShiftMutation();
  const [reOpenShift] = useReOpenShiftMutation();

  const [cancelledByType, setCancelledByType] = useState('');
  const [cancelEmailDialogOpen, setCancelEmailDialogOpen] = useState(false);
  const [cancelEmailShiftId, setCancelEmailShiftId] = useState(null);
  const [cancelEmailRecipient, setCancelEmailRecipient] = useState('');
  const [notifyShiftCancellation] = useNotifyShiftCancellationMutation();

  const notifiableStaff = useMemo(() => {
    if (!staffData) return [];
    return staffData
      .filter(u => (u.isManager || u.isAdmin) && u.isActive !== false)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [staffData]);

  useEffect(() => {
    if (!hasSetFluid.current) {
      setFluid(true);
      hasSetFluid.current = true;
    }
    return () => {
      if (hasSetFluid.current) {
        setFluid(false);
        hasSetFluid.current = false;
      }
    };
  }, [setFluid]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const weekParam = queryParams.get('week');
    if (weekParam) {
      try {
        const date = new Date(weekParam);
        if (!isNaN(date.getTime())) {
          setSelectedWeek(date);
        }
      } catch (error) {
        console.error('Invalid date in URL:', error);
      }
    }
  }, [location]);

  useEffect(() => {
    if (agenciesData) {
      const tierOrder = { 'Tier1': 1, 'Tier2': 2, 'Tier3': 3, 'Tier4': 4 };
      const sortedAgencies = [...agenciesData]
        .filter(agency => agency.isActive !== false)
        .sort((a, b) => {
          const tierA = tierOrder[a.agencyTier] || 999;
          const tierB = tierOrder[b.agencyTier] || 999;
          return tierA - tierB;
        });
      setAgencies(sortedAgencies);
      setFilteredAgenciesList(sortedAgencies);
    }
  }, [agenciesData]);

  useEffect(() => {
    if (shiftsData) {
      let uniqueWards = [...new Set(shiftsData.map(shift => shift.wardDepartmentArea))];

      if (isManager && !isAdmin && filteredWardNames.length > 0) {
        uniqueWards = uniqueWards.filter(ward => filteredWardNames.includes(ward));
      }

      setWards(uniqueWards);

      const shiftsByWardAndDay = {};
      uniqueWards.forEach(ward => {
        shiftsByWardAndDay[ward] = {};
        weekInfo.days.forEach(day => {
          shiftsByWardAndDay[ward][day.date] = [];
        });
      });

      const filteredShifts = shiftsData.filter(shift => {
        if (selectedReasonFilter !== 'all' && shift.reasonForRequest !== selectedReasonFilter) {
          return false;
        }
        if (isManager && !isAdmin && filteredWardNames.length > 0) {
          return filteredWardNames.includes(shift.wardDepartmentArea);
        }
        return true;
      });

      filteredShifts.forEach(shift => {
        const ward = shift.wardDepartmentArea;
        const startDateTime = new Date(shift.startDateTime);
        const shiftDate = toLocalDateKey(shift.startDateTime);
        const dayOfWeek = startDateTime.getDay();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

        const dateExistsInWeek = weekInfo.days.some(day => day.date === shiftDate);

        if (dateExistsInWeek && shiftsByWardAndDay[ward] && shiftsByWardAndDay[ward][shiftDate]) {
          shiftsByWardAndDay[ward][shiftDate].push({
            id: shift._id,
            startTime: startDateTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            endTime: new Date(shift.endDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            ward: ward,
            location: shift.hospitalLocationId?.name,
            staffName: shift.filledBy?.name || shift.filledByAgencyWorkerName || 'Available',
            staffJobTitle: shift.filledBy?.jobtitle || '',
            staffId: shift.filledBy?._id || shift.filledByAgency?._id,
            staffEmail: shift.filledBy?.email || shift.filledByAgencyEmail,
            isFilled: !!(shift.filledBy || shift.filledByAgency),
            isAgency: !!shift.filledByAgency,
            agencyName: shift.filledByAgencyName,
            agencyWorkerName: shift.filledByAgencyWorkerName,
            agencyWorkerContact: shift.filledByAgencyWorkerContact,
            isWeekend: isWeekend,
            categoryOfStaff: shift.categoryOfStaff || '',
            reasonForRequest: shift.reasonForRequest || '',
            reasonForRequestDetails: shift.reasonForRequestDetails || '',
            isOvertime: shift.isOvertime || false,
            poNumber: shift.poNumber || '',
            agencyCancelled: shift.agencyCancelled || false,
            actualStatus: shift.actualStatus || '',
            supersededByShiftId: shift.supersededByShiftId?._id || shift.supersededByShiftId || null,
            supersededByPoNumber: shift.supersededByShiftId?.poNumber || '',
            shiftData: shift
          });
        }
      });

      Object.keys(shiftsByWardAndDay).forEach(ward => {
        Object.keys(shiftsByWardAndDay[ward]).forEach(date => {
          shiftsByWardAndDay[ward][date].sort(
            (a, b) => new Date(a.shiftData.startDateTime) - new Date(b.shiftData.startDateTime)
          );
        });
      });

      setWardShifts(shiftsByWardAndDay);
    }
  }, [shiftsData, weekInfo.days, weekInfo.startDate, weekInfo.endDate, selectedReasonFilter, isManager, isAdmin, filteredWardNames]);

  useEffect(() => {
    if (availabilitiesData) {
      // no-op, reserved for future logging
    }
  }, [availabilitiesData]);

  // Group availabilities by staff member
  const getStaffAvailabilities = useCallback((staffId) => {
    if (!availabilitiesData || !Array.isArray(availabilitiesData)) return [];
    return availabilitiesData.filter(availability => {
      const availabilityStaffId = availability.requestedBy?._id || availability.requestedBy;
      return availabilityStaffId === staffId;
    });
  }, [availabilitiesData]);

  const staffHasAvailability = useCallback((staffId) => {
    return getStaffAvailabilities(staffId).length > 0;
  }, [getStaffAvailabilities]);

  const formatAvailabilityDisplay = useCallback((availability) => {
    if (!availability) return '';
    const startDate = new Date(availability.startDateTime);
    const endDate = new Date(availability.endDateTime);
    const dayName = startDate.toLocaleDateString('en-US', { weekday: 'long' });
    const dateStr = startDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
    const startTime = startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const endTime = endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const ward = availability.wardDepartmentArea || '';
    let display = `${dayName} ${dateStr} ${startTime}-${endTime}`;
    if (ward) display += ` (${ward})`;
    if (availability.isUnavailable || availability.isSickLeave) {
      const reason = availability.unavailabilityReason || availability.sicknessReason || 'Unavailable';
      display += ` [${reason.toUpperCase()}]`;
    }
    return display;
  }, []);

  useEffect(() => {
    if (editForm.hospitalLocationId && hospitalLocations.length > 0) {
      const selectedLocation = hospitalLocations.find((loc) => loc._id === editForm.hospitalLocationId);
      if (selectedLocation) {
        setEditForm(prev => ({ ...prev, wardDepartmentArea: selectedLocation.wardDepartmentArea || '' }));
      }
    }
  }, [editForm.hospitalLocationId, hospitalLocations]);

  // Check if staff has availability for a specific day
  const staffHasAvailabilityForDay = useCallback((staffId, dayName) => {
    if (!availabilitiesData || !Array.isArray(availabilitiesData) || dayName === 'all') {
      return true;
    }
    const staffAvailabilities = getStaffAvailabilities(staffId);
    if (staffAvailabilities.length === 0) {
      return false;
    }
    const dayObj = weekInfo.days.find(day => day.dayName === dayName);
    if (!dayObj) return false;
    const filterDate = dayObj.date;
    const hasAvailabilityOnDate = staffAvailabilities.some(availability => {
      const availabilityDate = toLocalDateKey(availability.startDateTime);
      const isSameDate = availabilityDate === filterDate;
      const isAvailable = !(availability.isUnavailable || availability.isSickLeave);
      return isSameDate && isAvailable;
    });
    return hasAvailabilityOnDate;
  }, [availabilitiesData, weekInfo.days, getStaffAvailabilities]);

  // NEW: check if a staff member is already assigned to ANY shift (any ward) on a given date.
  // Used to hide staff from the "available" panel when the day filter is active and they're
  // already booked that day — no point showing them as draggable if they can't take another shift.
  const isStaffAssignedOnDate = useCallback((staffId, dateStr) => {
    if (!staffId || !dateStr || !wardShifts) return false;
    return Object.keys(wardShifts).some(ward => {
      const shiftsForDay = wardShifts[ward]?.[dateStr] || [];
      return shiftsForDay.some(s => s.isFilled && !s.isAgency && s.staffId === staffId);
    });
  }, [wardShifts]);

  // Sort and filter staff with the day filter — also excludes staff already
  // assigned to a shift on the selected day.
  const sortedAndFilteredStaff = useMemo(() => {
    if (!staffData) return [];

    let filtered = staffData.filter(user =>
      !user.isAdmin && !user.isAgency && (isAdmin || !isAdminOnlyJobTitle(user.jobtitle))
    );

    if (selectedJobTitleFilter !== 'all') {
      filtered = filtered.filter(staff => staff.jobtitle === selectedJobTitleFilter);
    }

    if (selectedDayFilter !== 'all') {
      filtered = filtered.filter(staff => staffHasAvailabilityForDay(staff._id, selectedDayFilter));

      // Exclude staff who are already assigned to a shift on this day, in any ward —
      // they're not "available" for a new assignment even though they logged availability.
      const dayObj = weekInfo.days.find(day => day.dayName === selectedDayFilter);
      if (dayObj) {
        filtered = filtered.filter(staff => !isStaffAssignedOnDate(staff._id, dayObj.date));
      }
    }

    filtered = filtered.filter(staff => staffHasAvailability(staff._id));

    return filtered.sort((a, b) => {
      const aHasAvailability = staffHasAvailability(a._id);
      const bHasAvailability = staffHasAvailability(b._id);
      if (aHasAvailability && !bHasAvailability) return -1;
      if (!aHasAvailability && bHasAvailability) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [staffData, selectedJobTitleFilter, selectedDayFilter, staffHasAvailability, staffHasAvailabilityForDay, isStaffAssignedOnDate, weekInfo.days, isAdmin]);

  useEffect(() => {
    if (!staffSearchQuery.trim()) {
      setFilteredStaffList(sortedAndFilteredStaff);
      return;
    }
    const q = staffSearchQuery.trim().toLowerCase();
    setFilteredStaffList(
      sortedAndFilteredStaff.filter(staff =>
        staff.name?.toLowerCase().includes(q) || staff.jobtitle?.toLowerCase().includes(q)
      )
    );
  }, [sortedAndFilteredStaff, staffSearchQuery]);

  const staffWithAvailabilitiesCount = useMemo(() => {
    return filteredStaffList.filter(staff => staffHasAvailability(staff._id)).length;
  }, [filteredStaffList, staffHasAvailability]);

  const jobTitles = React.useMemo(() => {
    if (!staffData) return ['all'];
    const titles = [...new Set(staffData
      .filter(user => !user.isAdmin && !user.isAgency)
      .map(user => user.jobtitle)
      .filter(title => title && title.trim() !== '')
    )].sort();
    return ['all', ...titles];
  }, [staffData]);

  const dayOptions = React.useMemo(() => {
    const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    return ['all', ...allDays];
  }, []);

  // The date string (YYYY-MM-DD) for the selected day filter, e.g. this week's Sunday.
  // Used to scope both the ward-row filter below and the staff panel to that one date,
  // without hiding any of the seven day columns from the grid itself.
  const selectedDayFilterDate = useMemo(() => {
    if (selectedDayFilter === 'all') return null;
    const dayObj = weekInfo.days.find(day => day.dayName === selectedDayFilter);
    return dayObj ? dayObj.date : null;
  }, [weekInfo.days, selectedDayFilter]);

  const checkStaffHasUnavailability = useCallback((staffId, shiftDate, shiftStartDateTime, shiftEndDateTime) => {
    if (!availabilitiesData || !Array.isArray(availabilitiesData)) {
      return false;
    }
    const staffAvailabilities = availabilitiesData.filter(availability => {
      const availabilityStaffId = availability.requestedBy?._id || availability.requestedBy;
      return availabilityStaffId === staffId;
    });

    const hasUnavailabilityOnThisDate = staffAvailabilities.some(availability => {
      const availabilityDate = toLocalDateKey(availability.startDateTime);
      const isSameDate = availabilityDate === shiftDate;
      const isUnavailable = availability.isUnavailable === true || availability.isSickLeave === true;
      if (!isSameDate || !isUnavailable) return false;

      const unavailType = availability.unavailabilityType || availability.sickLeaveType || 'Full Day';
      if (unavailType === 'Full Day') return true;

      if (!shiftStartDateTime || !shiftEndDateTime) return true;

      const shiftStart = new Date(shiftStartDateTime);
      const shiftEnd = new Date(shiftEndDateTime);
      const unavailStart = new Date(availability.startDateTime);
      const unavailEnd = new Date(availability.endDateTime);

      return (
        (unavailStart <= shiftStart && shiftStart < unavailEnd) ||
        (unavailStart < shiftEnd && shiftEnd <= unavailEnd) ||
        (shiftStart <= unavailStart && shiftEnd >= unavailEnd)
      );
    });

    return hasUnavailabilityOnThisDate;
  }, [availabilitiesData]);

  const getStaffUnavailabilityInfo = useCallback((staffId) => {
    if (!availabilitiesData || !Array.isArray(availabilitiesData)) return [];
    const staffAvailabilities = availabilitiesData.filter(availability => {
      const availabilityStaffId = availability.requestedBy?._id || availability.requestedBy;
      return availabilityStaffId === staffId;
    });
    return staffAvailabilities
      .filter(avail => avail.isUnavailable || avail.isSickLeave)
      .map(avail => {
        const date = new Date(avail.startDateTime).toLocaleDateString();
        const isoDate = toLocalDateKey(avail.startDateTime);
        const reason = avail.unavailabilityReason || avail.sicknessReason || 'No reason specified';
        const type = avail.unavailabilityType || avail.sickLeaveType || 'Full Day';
        return { date, isoDate, reason, type };
      });
  }, [availabilitiesData]);

  const filteredWards = useMemo(() => {
    let result = selectedWardFilter === 'all' ? wards : wards.filter(ward => ward === selectedWardFilter);

    if (isManager && !isAdmin && filteredWardNames.length > 0) {
      result = result.filter(ward => filteredWardNames.includes(ward));
    }

    if (selectedReasonFilter !== 'all' && wardShifts) {
      const wardsWithMatchingReason = Object.keys(wardShifts).filter(ward => {
        const allShiftsInWard = Object.values(wardShifts[ward] || {}).flat();
        return allShiftsInWard.some(shift =>
          shift.reasonForRequest === selectedReasonFilter ||
          shift.shiftData?.reasonForRequest === selectedReasonFilter
        );
      });
      result = result.filter(ward => wardsWithMatchingReason.includes(ward));
    }

    // NEW: when a day filter is active, only show ward rows that have an unfilled
    // ("available") shift on that date — all 7 day columns stay visible, but wards whose
    // shifts on the selected day are already fully staffed (or that have no shift at all
    // that day) drop out of the grid entirely.
    if (selectedDayFilterDate) {
      result = result.filter(ward =>
        (wardShifts[ward]?.[selectedDayFilterDate] || []).some(s => !s.isFilled)
      );
    }

    return result;
  }, [selectedWardFilter, wards, selectedReasonFilter, wardShifts, isManager, isAdmin, filteredWardNames, selectedDayFilterDate]);

  const checkStaffHasSickLeave = useCallback((staffId, shiftDate) => {
    if (!availabilitiesData || !Array.isArray(availabilitiesData)) return false;
    const staffAvailabilities = availabilitiesData.filter(availability => {
      const availabilityStaffId = availability.requestedBy?._id || availability.requestedBy;
      return availabilityStaffId === staffId;
    });
    return staffAvailabilities.some(availability => {
      const availabilityDate = toLocalDateKey(availability.startDateTime);
      const isSameDate = availabilityDate === shiftDate;
      return isSameDate && availability.isSickLeave === true;
    });
  }, [availabilitiesData]);

  const handlePresetSelect = (presetKey) => {
    const preset = SHIFT_PRESETS[presetKey];
    if (preset) {
      setEditForm(prev => ({
        ...prev, selectedPreset: presetKey, startTime: preset.start, endTime: preset.end,
        escort: presetKey === 'ESCORT'
      }));
    }
  };

  const calculateShiftDuration = () => {
    if (!editForm.startDate || !editForm.startTime || !editForm.endDate || !editForm.endTime) {
      return 'Please enter dates and times';
    }
    const startDateTime = new Date(`${editForm.startDate}T${editForm.startTime}`);
    let endDateTime = new Date(`${editForm.endDate}T${editForm.endTime}`);
    if (editForm.endTime < editForm.startTime && editForm.startDate === editForm.endDate) {
      endDateTime.setDate(endDateTime.getDate() + 1);
    }
    if (endDateTime <= startDateTime) {
      return 'Invalid: End time must be after start time';
    }
    const durationMs = endDateTime - startDateTime;
    const durationHours = Math.floor(durationMs / (1000 * 60 * 60));
    const durationMinutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${durationHours} hours ${durationMinutes} minutes`;
  };

  const getFilterSummary = () => {
    const filters = [];
    if (selectedWardFilter !== 'all') filters.push(`Ward: ${selectedWardFilter}`);
    if (selectedJobTitleFilter !== 'all') filters.push(`Job: ${selectedJobTitleFilter}`);
    if (selectedDayFilter !== 'all') filters.push(`Day: ${selectedDayFilter}`);
    if (selectedReasonFilter !== 'all') {
      const reasonLabel = REASON_FOR_REQUEST.find(r => r.value === selectedReasonFilter)?.label || selectedReasonFilter;
      filters.push(`Reason: ${reasonLabel}`);
    }
    if (isManager && !isAdmin && managerDirectorate) {
      filters.push(`Directorate: ${managerDirectorate}`);
    }
    return filters.length > 0 ? filters.join(' • ') : 'No filters applied';
  };

  const clearAllFilters = () => {
    setSelectedWardFilter('all');
    setSelectedJobTitleFilter('all');
    setSelectedDayFilter('all');
    setSelectedReasonFilter('all');
  };

  const handleDragStart = (e, staff) => {
    setDraggedStaff(staff);
    setDraggedAgency(null);
    e.dataTransfer.setData('text/plain', JSON.stringify(staff));
    e.dataTransfer.setData('sourceType', 'staff');
    const unavailabilityInfo = getStaffUnavailabilityInfo(staff._id);
    if (unavailabilityInfo.length > 0) {
      e.dataTransfer.setData('unavailabilityWarning', 'Staff has unavailability marked');
    }
  };

  const handleAgencyDragStart = (e, agency) => {
    setDraggedAgency(agency);
    setDraggedStaff(null);
    e.dataTransfer.setData('text/plain', JSON.stringify(agency));
    e.dataTransfer.setData('sourceType', 'agency');
  };

  const checkStaffAvailabilityForShift = useCallback((staffId, shift) => {
    if (!staffId || !shift || !shift.shiftData) {
      return false;
    }
    const shiftDate = toLocalDateKey(shift.shiftData.startDateTime);
    const hasUnavailability = checkStaffHasUnavailability(staffId, shiftDate, shift.shiftData.startDateTime, shift.shiftData.endDateTime);
    if (hasUnavailability) {
      return false;
    }

    const staffAvailabilities = getStaffAvailabilities(staffId);
    if (staffAvailabilities.length === 0) {
      return false;
    }

    const shiftStart = new Date(shift.shiftData.startDateTime);
    const shiftEnd = new Date(shift.shiftData.endDateTime);
    const shiftCategory = shift.shiftData?.categoryOfStaff;

    const staff = staffData?.find(s => s._id === staffId);
    const staffJobTitle = staff?.jobtitle;

    for (const availability of staffAvailabilities) {
      if (availability.isUnavailable || availability.isSickLeave) {
        continue;
      }
      const availabilityStart = new Date(availability.startDateTime);
      const availabilityEnd = new Date(availability.endDateTime);
      const availabilityDate = toLocalDateKey(availability.startDateTime);

      if (availabilityDate === shiftDate) {
        const shiftStartTime = shiftStart.getTime();
        const shiftEndTime = shiftEnd.getTime();
        const availabilityStartTime = availabilityStart.getTime();
        const availabilityEndTime = availabilityEnd.getTime();

        const timeMatches = shiftStartTime >= availabilityStartTime && shiftEndTime <= availabilityEndTime;

        if (timeMatches) {
          if (shiftCategory && staffJobTitle) {
            const shiftHasHCA = shiftCategory.includes('HCA');
            const staffHasHCA = staffJobTitle.includes('HCA');
            if (shiftHasHCA && staffHasHCA) {
              return true;
            }
            const categoryMatch = staffJobTitle.includes(shiftCategory) ||
              shiftCategory.includes(staffJobTitle) ||
              staffJobTitle === shiftCategory;
            if (!categoryMatch) {
              return false;
            }
          } else if (shiftCategory && !staffJobTitle) {
            return false;
          }
          return true;
        }
      }
    }
    return false;
  }, [getStaffAvailabilities, checkStaffHasUnavailability, staffData]);

  const handleDragOver = (e) => { e.preventDefault(); };

  const handleDrop = async (e, shift) => {
    e.preventDefault();
    if (shift.isFilled) {
      showSnackbar('This shift is already filled', 'warning');
      return;
    }
    const sourceType = e.dataTransfer.getData('sourceType');
    if (sourceType === 'staff' && draggedStaff) {
      await handleStaffDrop(draggedStaff, shift);
    } else if (sourceType === 'agency' && draggedAgency) {
      await handleAgencyDrop(draggedAgency, shift);
    }
  };

  const handleStaffDrop = async (staff, shift) => {
    const shiftDate = toLocalDateKey(shift.shiftData.startDateTime);
    const hasUnavailability = checkStaffHasUnavailability(staff._id, shiftDate, shift.shiftData.startDateTime, shift.shiftData.endDateTime);

    if (hasUnavailability) {
      const unavailabilityInfo = getStaffUnavailabilityInfo(staff._id);
      const todayUnavailability = unavailabilityInfo.find(unavail => unavail.isoDate === shiftDate);
      if (todayUnavailability) {
        showSnackbar(`${staff.name} is unavailable on ${shiftDate}. Reason: ${todayUnavailability.reason}`, 'error');
      } else {
        showSnackbar(`${staff.name} is marked as unavailable on this date. Cannot assign to shift.`, 'error');
      }
      return;
    }

    if (staff.wardExclusions?.length > 0) {
      const shiftWard = shift.ward;
      const isExcluded = staff.wardExclusions.some(
        excludedWard => excludedWard.toLowerCase() === (shiftWard || '').toLowerCase()
      );
      if (isExcluded) {
        showSnackbar(`${staff.name} is excluded from ${shiftWard} and cannot be assigned to this shift`, 'error');
        return;
      }
    }

    const hasAvailability = checkStaffAvailabilityForShift(staff._id, shift);

    if (!hasAvailability) {
      const shiftCategory = shift.shiftData?.categoryOfStaff;
      const staffJobTitle = staff.jobtitle;
      let errorMessage = `${staff.name} does not have availability for this shift`;

      if (shiftCategory && staffJobTitle && shiftCategory !== staffJobTitle) {
        errorMessage = `${staff.name} (${staffJobTitle}) does not match required category: ${shiftCategory}`;
      } else {
        const staffAvailabilities = getStaffAvailabilities(staff._id);
        const sDate = toLocalDateKey(shift.shiftData.startDateTime);
        const hasAvailabilityOnDate = staffAvailabilities.some(avail => {
          const availDate = toLocalDateKey(avail.startDateTime);
          return availDate === sDate && !(avail.isUnavailable || avail.isSickLeave);
        });
        if (!hasAvailabilityOnDate) {
          errorMessage = `${staff.name} has no availability on ${sDate}`;
        } else {
          errorMessage = `${staff.name} has availability but times don't match`;
        }
      }
      showSnackbar(errorMessage, 'error');
      return;
    }

    try {
      await assignStaff({
        shiftId: shift.id,
        staffData: {
          userId: staff._id,
          userName: staff.name,
          userEmail: staff.email,
          managerId: userInfo._id
        }
      }).unwrap();

      const date = toLocalDateKey(shift.shiftData.startDateTime);
      const updatedShifts = { ...wardShifts };

      if (updatedShifts[shift.ward] && updatedShifts[shift.ward][date]) {
        const shiftIndex = updatedShifts[shift.ward][date].findIndex(s => s.id === shift.id);
        if (shiftIndex !== -1) {
          updatedShifts[shift.ward][date][shiftIndex] = {
            ...updatedShifts[shift.ward][date][shiftIndex],
            staffName: staff.name,
            staffId: staff._id,
            staffEmail: staff.email,
            isFilled: true,
            isAgency: false
          };
          setWardShifts(updatedShifts);
        }
      }

      refetchShifts();
      setDraggedStaff(null);
      showSnackbar(`${staff.name} assigned to shift successfully!`, 'success');
    } catch (error) {
      console.error('Failed to assign staff to shift:', error);
      showSnackbar('Failed to assign staff to shift', 'error');
    }
  };

  const handleAgencyDrop = async (agency, shift) => {
    if (agency.isActive === false) {
      showSnackbar('This agency is not active', 'error');
      return;
    }
    setSelectedShiftForAgency(shift);
    setSelectedAgency(agency);
    setAgencyWorkerDetails({
      workerName: '',
      workerContact: agency.agencyPhone || '',
      workerEmail: agency.agencyEmail || '',
    });
    setAgencyWorkerDialogOpen(true);
  };

  const handleAgencyAssignment = async () => {
    if (!selectedShiftForAgency || !selectedAgency) return;

    if (!agencyWorkerDetails.workerName.trim()) {
      showSnackbar('Please enter the agency worker\'s name', 'error');
      return;
    }
    if (!agencyWorkerDetails.workerContact.trim()) {
      showSnackbar('Please enter the agency worker\'s contact number', 'error');
      return;
    }

    try {
      const shift = selectedShiftForAgency;
      const agency = selectedAgency;

      const assignResult = await assignAgencyToShift({
        shiftId: shift.id,
        agencyData: {
          agencyId: agency._id,
          agencyName: agency.agencyName,
          agencyEmail: agency.agencyEmail,
          workerName: agencyWorkerDetails.workerName,
          workerContact: agencyWorkerDetails.workerContact,
          workerEmail: agencyWorkerDetails.workerEmail || agency.agencyEmail,
          managerId: userInfo._id
        }
      }).unwrap();

      const newPoNumber = assignResult?.shift?.poNumber || assignResult?.poNumber || '';

      const date = new Date(shift.shiftData.startDateTime).toISOString().split('T')[0];
      const updatedShifts = { ...wardShifts };

      if (updatedShifts[shift.ward] && updatedShifts[shift.ward][date]) {
        const shiftIndex = updatedShifts[shift.ward][date].findIndex(s => s.id === shift.id);
        if (shiftIndex !== -1) {
          updatedShifts[shift.ward][date][shiftIndex] = {
            ...updatedShifts[shift.ward][date][shiftIndex],
            staffName: agencyWorkerDetails.workerName,
            staffId: agency._id,
            staffEmail: agencyWorkerDetails.workerEmail || agency.agencyEmail,
            isFilled: true,
            isAgency: true,
            agencyName: agency.agencyName,
            agencyWorkerName: agencyWorkerDetails.workerName,
            agencyWorkerContact: agencyWorkerDetails.workerContact,
          };
          setWardShifts(updatedShifts);
        }
      }

      refetchShifts();

      showSnackbar(
        `${agencyWorkerDetails.workerName} from ${agency.agencyName} assigned to shift successfully!${newPoNumber ? ` PO: ${newPoNumber}` : ''}`,
        'success'
      );
      setAgencyWorkerDialogOpen(false);
      setSelectedShiftForAgency(null);
      setSelectedAgency(null);
      setDraggedAgency(null);
    } catch (error) {
      console.error('Failed to assign agency to shift:', error);
      showSnackbar(error?.data?.message || 'Failed to assign agency to shift', 'error');
    }
  };

  const handleEditShiftClick = (shift) => {
    if (!shift || !shift.shiftData) return;
    const shiftData = shift.shiftData;

    const startTime = dublinTimeStr(shiftData.startDateTime);
    const endTime = dublinTimeStr(shiftData.endDateTime);

    let selectedPreset = '';
    Object.entries(SHIFT_PRESETS).forEach(([key, preset]) => {
      if (preset.start === startTime && preset.end === endTime) {
        selectedPreset = key;
      }
    });

    setShiftToEdit({
      shiftId: shift.id,
      ward: shift.ward,
      dayDate: toLocalDateKey(shiftData.startDateTime),
      staffName: shift.staffName,
      staffId: shift.staffId,
      isAgency: shift.isAgency
    });

    let hospitalLocationId = '';
    if (shiftData.hospitalLocationId) {
      if (typeof shiftData.hospitalLocationId === 'object' && shiftData.hospitalLocationId._id) {
        hospitalLocationId = shiftData.hospitalLocationId._id;
      } else if (typeof shiftData.hospitalLocationId === 'string') {
        hospitalLocationId = shiftData.hospitalLocationId;
      }
    }

    if ((!hospitalLocationId || hospitalLocationId === '') && shiftData.wardDepartmentArea && hospitalLocations.length > 0) {
      const locationByWard = hospitalLocations.find(loc => loc.wardDepartmentArea === shiftData.wardDepartmentArea);
      if (locationByWard) {
        hospitalLocationId = locationByWard._id;
      }
    }

    setEditForm({
      startDate: toLocalDateKey(shiftData.startDateTime),
      startTime: startTime,
      endDate: toLocalDateKey(shiftData.endDateTime),
      endTime: endTime,
      selectedPreset: selectedPreset,
      hospitalLocationId: hospitalLocationId,
      wardDepartmentArea: shiftData.wardDepartmentArea || '',
      categoryOfStaff: shiftData.categoryOfStaff || '',
      reasonForRequest: shiftData.reasonForRequest || '',
      reasonForRequestDetails: shiftData.reasonForRequestDetails || '',
      escort: shiftData.escort || false,
      special: shiftData.special || false,
      isOvertime: shiftData.isOvertime || false,
      isActive: shiftData.isActive !== false,
      eccNumber: shiftData.eccNumber || '',
      specialRequirements: shiftData.specialRequirements || '',
      comments: shiftData.comments || '',
      workerName: shiftData.filledByAgencyWorkerName || '',
      workerContact: shiftData.filledByAgencyWorkerContact || '',
      workerEmail: shiftData.filledByAgencyWorkerEmail || '',
      specialsReason: shiftData.specialsReason || '',
    });

    setActiveTab('basic');
    setEditDialogOpen(true);
  };

  const openCancelDialogForShift = (shift) => {
    if (!shift || !shift.isFilled) return;
    if (shift.agencyCancelled) return;

    setShiftToRelease({
      shiftId: shift.id,
      staffId: shift.staffId,
      staffName: shift.staffName,
      isAgency: shift.isAgency,
      agencyName: shift.agencyName,
      poNumber: shift.poNumber,
      ward: shift.ward,
      dayDate: toLocalDateKey(shift.shiftData.startDateTime),
      shiftTime: `${shift.startTime} - ${shift.endTime}`,
      shiftStartDateTime: shift.shiftData.startDateTime,
    });
    setCancelledByType('');
    setAdvanceNoticeGiven(null);
    setReleaseDialogOpen(true);
  };

  const handleShiftDoubleClick = (shift) => {
    openCancelDialogForShift(shift);
  };

  const handleReleaseShift = async () => {
    if (!shiftToRelease) return;

    if (shiftToRelease.isAgency && !cancelledByType) {
      showSnackbar('Please select who is cancelling this shift', 'error');
      return;
    }
    if (shiftToRelease.isAgency && cancelledByType === 'Beaumont' && advanceNoticeGiven === null) {
      showSnackbar('Please confirm whether the agency was given 12 hours notice', 'error');
      return;
    }

    try {
      const result = await reOpenShift({
        shiftId: shiftToRelease.shiftId,
        userId: shiftToRelease.staffId,
        userName: shiftToRelease.staffName,
        userEmail: '',
        managerId: userInfo._id,
        ...(shiftToRelease.isAgency && {
          cancelledByType,
          ...(cancelledByType === 'Beaumont' && { advanceNoticeGiven }),
        }),
      }).unwrap();

      const updatedShifts = { ...wardShifts };
      if (updatedShifts[shiftToRelease.ward] && updatedShifts[shiftToRelease.ward][shiftToRelease.dayDate]) {
        const shiftIndex = updatedShifts[shiftToRelease.ward][shiftToRelease.dayDate].findIndex(
          s => s.id === shiftToRelease.shiftId
        );
        if (shiftIndex !== -1) {
          if (shiftToRelease.isAgency) {
            updatedShifts[shiftToRelease.ward][shiftToRelease.dayDate][shiftIndex] = {
              ...updatedShifts[shiftToRelease.ward][shiftToRelease.dayDate][shiftIndex],
              agencyCancelled: true,
              cancelledByType,
              chargeApplies: result?.shift?.chargeApplies || false,
            };
          } else {
            updatedShifts[shiftToRelease.ward][shiftToRelease.dayDate][shiftIndex] = {
              ...updatedShifts[shiftToRelease.ward][shiftToRelease.dayDate][shiftIndex],
              staffName: 'Available',
              staffId: null,
              staffEmail: null,
              isFilled: false,
              isAgency: false,
              agencyName: null,
              agencyWorkerName: null,
              agencyWorkerContact: null,
              isOvertime: false,
            };
          }
          setWardShifts(updatedShifts);
        }
      }

      refetchShifts();

      if (shiftToRelease.isAgency) {
        showSnackbar(
          `Agency shift marked as cancelled (by ${cancelledByType}).${result?.shift?.chargeApplies ? ' A charge applies.' : ''} ${shiftToRelease.poNumber ? `PO ${shiftToRelease.poNumber}. ` : ''}Contact the agency directly to cancel the booking.`,
          result?.shift?.chargeApplies ? 'warning' : 'info'
        );
        setCancelEmailShiftId(shiftToRelease.shiftId);
        setCancelEmailRecipient('');
        setCancelEmailDialogOpen(true);
      } else {
        showSnackbar('Staff member unassigned successfully! Shift is now available.', 'success');
      }
    } catch (error) {
      console.error('Failed to release shift:', error);
      showSnackbar(error?.data?.message || 'Failed to release shift', 'error');
    } finally {
      setReleaseDialogOpen(false);
      setShiftToRelease(null);
      setCancelledByType('');
      setAdvanceNoticeGiven(null);
    }
  };

  const handleCancelRelease = () => {
    setReleaseDialogOpen(false);
    setShiftToRelease(null);
  };

  const handleCreateShiftClick = (ward, day) => {
    const hospitalLocation = filteredHospitalLocations.find(loc => loc.wardDepartmentArea === ward);

    setNewShiftInfo({ ward: ward, dayDate: day.date, dayInfo: day });

    const startTime = '08:00';
    const endTime = '20:00';
    const startDate = day.date;
    const endDate = day.date;

    setEditForm({
      startDate: startDate,
      startTime: startTime,
      endDate: endDate,
      endTime: endTime,
      selectedPreset: 'LD',
      hospitalLocationId: hospitalLocation?._id || '',
      wardDepartmentArea: ward || '',
      categoryOfStaff: '',
      reasonForRequest: '',
      reasonForRequestDetails: '',
      escort: false,
      special: false,
      isOvertime: false,
      isActive: true,
      eccNumber: '',
      specialRequirements: '',
      comments: `New shift for ${ward} on ${day.dayName}`,
      specialsReason: editForm.specialsReason || '',
    });

    setActiveTab('schedule');
    setCreateDialogOpen(true);
  };

  const handleCreateShift = async () => {
    if (!newShiftInfo.ward || !newShiftInfo.dayDate) return;

    try {
      const addOneDay = (dateStr) => {
        const [y, m, d] = dateStr.split('-').map(Number);
        const dt = new Date(Date.UTC(y, m - 1, d));
        dt.setUTCDate(dt.getUTCDate() + 1);
        return dt.toISOString().split('T')[0];
      };
      const isOvernight = editForm.endTime < editForm.startTime && editForm.startDate === editForm.endDate;
      const resolvedEndDate = isOvernight ? addOneDay(editForm.endDate) : editForm.endDate;

      const shiftData = {
        startDateTime: dublinLocalToUtcISO(editForm.startDate, editForm.startTime),
        endDateTime: dublinLocalToUtcISO(resolvedEndDate, editForm.endTime),
        hospitalLocationId: editForm.hospitalLocationId,
        wardDepartmentArea: editForm.wardDepartmentArea,
        reasonForRequest: editForm.reasonForRequest,
        reasonForRequestDetails: editForm.reasonForRequestDetails,
        categoryOfStaff: editForm.categoryOfStaff,
        escort: editForm.escort,
        special: editForm.special,
        isOvertime: editForm.isOvertime,
        isActive: editForm.isActive,
        eccNumber: editForm.eccNumber,
        specialRequirements: editForm.specialRequirements,
        comments: editForm.comments,
        specialsReason: editForm.specialsReason || '',
      };

      const result = await createShift({ numberOfShifts: 1, shiftDates: [], ...shiftData }).unwrap();

      refetchShifts();

      showSnackbar(`New shift created successfully in ${newShiftInfo.ward}!`, 'success');
      setCreateDialogOpen(false);
      setNewShiftInfo({ ward: '', dayDate: '', dayInfo: null });
    } catch (error) {
      console.error('Failed to create shift:', error);
      showSnackbar(error?.data?.message || 'Failed to create shift', 'error');
    }
  };

  const handleUpdateShift = async () => {
    if (!shiftToEdit || !shiftToEdit.shiftId) return;

    try {
      const addOneDay = (dateStr) => {
        const [y, m, d] = dateStr.split('-').map(Number);
        const dt = new Date(Date.UTC(y, m - 1, d));
        dt.setUTCDate(dt.getUTCDate() + 1);
        return dt.toISOString().split('T')[0];
      };
      const isOvernight = editForm.endTime < editForm.startTime && editForm.startDate === editForm.endDate;
      const resolvedEndDate = isOvernight ? addOneDay(editForm.endDate) : editForm.endDate;

      const formattedData = {
        shiftId: shiftToEdit.shiftId,
        startDateTime: dublinLocalToUtcISO(editForm.startDate, editForm.startTime),
        endDateTime: dublinLocalToUtcISO(resolvedEndDate, editForm.endTime),
        hospitalLocationId: editForm.hospitalLocationId,
        wardDepartmentArea: editForm.wardDepartmentArea,
        reasonForRequest: editForm.reasonForRequest,
        reasonForRequestDetails: editForm.reasonForRequestDetails,
        categoryOfStaff: editForm.categoryOfStaff,
        escort: editForm.escort,
        special: editForm.special,
        isOvertime: editForm.isOvertime,
        isActive: editForm.isActive,
        eccNumber: editForm.eccNumber,
        specialRequirements: editForm.specialRequirements,
        specialsReason: editForm.specialsReason || '',
        comments: editForm.comments,
        ...(shiftToEdit.isAgency && {
          filledByAgencyWorkerName: editForm.workerName,
          filledByAgencyWorkerContact: editForm.workerContact,
          filledByAgencyWorkerEmail: editForm.workerEmail,
          filledByName: editForm.workerName,
        }),
      };

      const result = await updateShift(formattedData).unwrap();

      const updatedShifts = { ...wardShifts };
      if (updatedShifts[shiftToEdit.ward] && updatedShifts[shiftToEdit.ward][shiftToEdit.dayDate]) {
        const shiftIndex = updatedShifts[shiftToEdit.ward][shiftToEdit.dayDate].findIndex(
          s => s.id === shiftToEdit.shiftId
        );
        if (shiftIndex !== -1) {
          const updatedShift = {
            ...updatedShifts[shiftToEdit.ward][shiftToEdit.dayDate][shiftIndex],
            startTime: new Date(formattedData.startDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            endTime: new Date(formattedData.endDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            ward: editForm.wardDepartmentArea,
            categoryOfStaff: editForm.categoryOfStaff,
            ...(shiftToEdit.isAgency && {
              staffName: editForm.workerName,
              agencyWorkerName: editForm.workerName,
              agencyWorkerContact: editForm.workerContact,
            }),
          };
          updatedShifts[shiftToEdit.ward][shiftToEdit.dayDate][shiftIndex] = updatedShift;
          setWardShifts(updatedShifts);
        }
      }

      refetchShifts();

      showSnackbar('Shift updated successfully!', 'success');
      setEditDialogOpen(false);
      setShiftToEdit(null);
    } catch (error) {
      console.error('=== UPDATE SHIFT ERROR ===', error);
      showSnackbar(error?.data?.message || error?.message || 'Failed to update shift', 'error');
    }
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setShiftToEdit(null);
  };

  const handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm(prev => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'reasonForRequest' && !['1-1 special', 'Cohorts'].some(r => value?.toLowerCase() === r.toLowerCase())) {
        next.specialsReason = '';
      }
      return next;
    });
  };

  const formatTimeUTC = (dateTimeStr) => {
    const d = new Date(dateTimeStr);
    return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
  };

  const getStaffSickLeaveInfo = useCallback((staffId) => {
    if (!availabilitiesData || !Array.isArray(availabilitiesData)) return [];
    const staffAvailabilities = availabilitiesData.filter(availability => {
      const availabilityStaffId = availability.requestedBy?._id || availability.requestedBy;
      return availabilityStaffId === staffId;
    });
    return staffAvailabilities
      .filter(avail => avail.isSickLeave)
      .map(avail => {
        const date = new Date(avail.startDateTime).toLocaleDateString();
        const reason = avail.sicknessReason || 'No reason specified';
        return { date, reason };
      });
  }, [availabilitiesData]);

  // PRINT FUNCTION (kept scoped to the full week regardless of the on-screen day filter,
  // since a printed schedule is generally expected to show the whole week)
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      showSnackbar('Please allow pop-ups to print', 'warning');
      return;
    }

    const filterInfo = getFilterSummary();

    const printHTML = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Ward Weekly Schedule - ${formatDateRange()}</title>
        <style>
          @page { size: landscape; margin: 0.5in; }
          body { font-family: Arial, sans-serif; margin: 0; padding: 20px; background: white;
            -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
          .print-header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #000; padding-bottom: 10px; }
          .print-title { font-size: 24px; font-weight: bold; margin-bottom: 5px; }
          .print-subtitle { font-size: 16px; color: #666; margin-bottom: 10px; }
          .print-info { font-size: 12px; color: #666; margin-bottom: 5px; }
          .print-calendar { width: 100%; border-collapse: collapse; table-layout: fixed; margin-top: 20px; }
          .print-calendar th { background-color: #1976d2; color: white; padding: 10px; text-align: center; border: 1px solid #ddd; font-weight: bold; }
          .print-calendar td { padding: 5px; border: 1px solid #ddd; vertical-align: top; min-height: 120px; }
          .ward-header { background-color: #1976d2; color: white; padding: 8px; text-align: center; font-weight: bold; width: 200px; }
          .day-header { background-color: #1976d2; color: white; padding: 8px; text-align: center; font-weight: bold; }
          .weekend-header { background-color: #ff9800 !important; }
          .ward-cell { padding: 5px; border-bottom: 1px solid #eee; }
          .shift-card { padding: 4px; margin: 2px 0; border-radius: 4px; font-size: 10px; color: white; font-weight: bold;
            page-break-inside: avoid; break-inside: avoid; min-height: 50px; }
          .shift-assigned { background-color: #66bb6a !important; border: 1px solid #388e3c; }
          .shift-evening-assigned { background-color: #2e7d32 !important; border: 1px solid #1b5e20; }
          .shift-agency-assigned { background-color: #9c27b0 !important; border: 1px solid #7b1fa2; }
          .shift-agency-evening { background-color: #6a0080 !important; border: 1px solid #4a0060; }
          .shift-available { background-color: #64b5f6 !important; border: 1px solid #1e88e5; }
          .shift-evening-available { background-color: #1565c0 !important; border: 1px solid #0d47a1; }
          .shift-time { font-weight: bold; font-size: 9px; }
          .shift-details { font-size: 8px; margin-top: 2px; }
          .staff-name { font-size: 9px; font-weight: bold; }
          .agency-badge { font-size: 7px; font-style: italic; background-color: rgba(255,255,255,0.3); padding: 1px 3px; border-radius: 2px; display: inline-block; margin-top: 2px; }
          .job-title-badge { font-size: 7px; background-color: rgba(255,255,255,0.25); padding: 1px 3px; border-radius: 2px; display: inline-block; margin-top: 2px; }
          .ward-info { padding: 8px; background-color: #f5f5f5; border-right: 1px solid #ddd; min-width: 200px; }
          .ward-name { font-weight: bold; font-size: 12px; }
          .ward-stats { font-size: 10px; color: #666; margin-top: 2px; }
          .legend { margin-top: 30px; padding: 15px; border: 1px solid #ddd; background-color: #f9f9f9; page-break-inside: avoid; break-inside: avoid; }
          .legend-title { font-weight: bold; margin-bottom: 10px; font-size: 14px; }
          .legend-item { display: inline-block; margin-right: 20px; margin-bottom: 10px; }
          .legend-color { display: inline-block; width: 20px; height: 20px; margin-right: 5px; vertical-align: middle; border-radius: 3px; border: 1px solid #ddd; }
          .footer { margin-top: 20px; padding-top: 10px; border-top: 1px solid #ddd; font-size: 10px; color: #666; text-align: center; }
          @media print {
            .no-print { display: none !important; }
            body { margin: 0 !important; padding: 0 !important; }
            .print-calendar { width: 100% !important; font-size: 10px !important; }
            .shift-card { page-break-inside: avoid !important; break-inside: avoid !important; }
            .ward-row { page-break-inside: avoid !important; break-inside: avoid !important; }
          }
        </style>
      </head>
      <body>
        <div class="print-header">
          <div class="print-title">Ward Weekly Schedule</div>
          <div class="print-subtitle">Week of ${formatDateRange()}</div>
          <div class="print-info">Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</div>
          <div class="print-info">Generated By: ${userInfo?.name || 'Manager'}</div>
          ${filterInfo !== 'No filters applied' ? `<div class="print-info">Filters: ${filterInfo}</div>` : ''}
          <div class="print-info">Showing ${filteredWards.length} wards</div>
        </div>

        <table class="print-calendar">
          <thead>
            <tr>
              <th class="ward-header">Ward Information</th>
              ${weekInfo.days.map(day => `
                <th class="day-header ${day.isWeekend ? 'weekend-header' : ''}" style="width: calc((100% - 200px) / 7);">
                  ${day.dayName}<br>
                  <span style="font-size: 11px; font-weight: normal;">${day.date}</span>
                  ${day.isWeekend ? '<div style="font-size: 10px; font-style: italic;">Weekend</div>' : ''}
                </th>
              `).join('')}
            </tr>
          </thead>
          <tbody>
            ${filteredWards.map((ward, index) => {
              const wardShiftsData = wardShifts[ward] || {};
              const allShifts = Object.values(wardShiftsData).flat();
              const totalShifts = allShifts.length;
              const filledShifts = allShifts.filter(s => s.isFilled).length;
              const agencyShifts = allShifts.filter(s => s.isAgency).length;
              const staffShifts = filledShifts - agencyShifts;
              const availableShifts = allShifts.filter(s => !s.isFilled).length;
              const weekendShifts = allShifts.filter(s => s.isWeekend).length;

              return `
                <tr class="ward-row">
                  <td class="ward-info" style="background-color: ${index % 2 === 0 ? '#f9f9f9' : 'white'};">
                    <div class="ward-name">${ward}</div>
                    <div class="ward-stats">
                      Total: ${totalShifts} shift${totalShifts !== 1 ? 's' : ''}<br>
                      Filled: ${filledShifts} (Staff: ${staffShifts} | Agency: ${agencyShifts})<br>
                      Available: ${availableShifts}<br>
                      Weekend: ${weekendShifts}
                    </div>
                  </td>
                  ${weekInfo.days.map(day => {
                    const shifts = wardShiftsData[day.date] || [];
                    return `
                      <td style="background-color: ${day.isWeekend ? 'rgba(255, 152, 0, 0.05)' : (index % 2 === 0 ? '#f9f9f9' : 'white')};">
                        ${shifts.map(shift => {
                          const isEvening = isEveningShift(shift.startTime);
                          let shiftClass = 'shift-card';
                          if (shift.agencyCancelled) {
                            shiftClass += ' shift-cancelled';
                          } else if (shift.isFilled) {
                            if (shift.isAgency) {
                              shiftClass += isEvening ? ' shift-agency-evening' : ' shift-agency-assigned';
                            } else {
                              shiftClass += isEvening ? ' shift-evening-assigned' : ' shift-assigned';
                            }
                          } else {
                            shiftClass += isEvening ? ' shift-evening-available' : ' shift-available';
                          }
                          const categoryOfStaff = shift.categoryOfStaff || shift.shiftData?.categoryOfStaff || '';
                          return `
                            <div class="${shiftClass}">
                              <div class="shift-time">${shift.startTime} - ${shift.endTime}</div>
                              <div class="staff-name">${shift.staffName}</div>
                              ${shift.staffJobTitle && !shift.isAgency ? `<div class="job-title-badge">${shift.staffJobTitle}</div>` : ''}
                              ${shift.isAgency ? `<div class="agency-badge">via ${shift.agencyName || 'Agency'}</div>` : ''}
                              <div style="display: flex; align-items: center; gap: 4px; margin-top: 2px; font-size: 8px;">
                                ${categoryOfStaff ? `<span style="font-style: italic; opacity: 0.9; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1;">${categoryOfStaff.length > 10 ? categoryOfStaff.substring(0, 8) + '...' : categoryOfStaff}</span>` : ''}
                                ${shift.isOvertime ? `<span style="background-color: #ff9800; color: white; font-weight: bold; padding: 2px 4px; border-radius: 4px; font-size: 7px; display: inline-block; flex-shrink: 0;">OVERTIME</span>` : ''}
                              </div>
                              <div class="shift-details">
                                Status: ${shift.isFilled ? (shift.isAgency ? 'Agency' : 'Assigned') : 'Available'}
                                ${shift.isWeekend ? '• Weekend' : ''}
                              </div>
                              ${shift.location ? `<div class="shift-details">${shift.location}</div>` : ''}
                            </div>
                          `;
                        }).join('')}
                        ${shifts.length === 0 ? `<div style="text-align: center; color: #999; font-style: italic; font-size: 10px; padding: 20px 5px;">No shifts</div>` : ''}
                      </td>
                    `;
                  }).join('')}
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>

        <div class="legend">
          <div class="legend-title">Color Legend:</div>
          <div class="legend-item"><span class="legend-color" style="background-color: #66bb6a;"></span><span>Filled — Day Shift (before 18:00)</span></div>
          <div class="legend-item"><span class="legend-color" style="background-color: #2e7d32;"></span><span>Filled — Evening/Night Shift (18:00+)</span></div>
          <div class="legend-item"><span class="legend-color" style="background-color: #9c27b0;"></span><span>Agency — Day Shift</span></div>
          <div class="legend-item"><span class="legend-color" style="background-color: #6a0080;"></span><span>Agency — Evening/Night Shift</span></div>
          <div class="legend-item"><span class="legend-color" style="background-color: #64b5f6;"></span><span>Available — Day Shift (before 18:00)</span></div>
          <div class="legend-item"><span class="legend-color" style="background-color: #1565c0;"></span><span>Available — Evening/Night Shift (18:00+)</span></div>
          <div class="legend-item"><span class="legend-color" style="background-color: #e65100;"></span><span>Agency — Cancelled (contact agency to finalise)</span></div>
        </div>

        <div class="footer">
          Beaumont Hospital Ward Scheduling System<br>
          Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}<br>
          For questions, contact: beaumontNursingBank@beaumont.ie
        </div>

        <script>
          window.onload = function() {
            window.print();
            setTimeout(function() { window.close(); }, 100);
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(printHTML);
    printWindow.document.close();
  };

  const getEmailDays = () => {
    if (includeWeekend) {
      return weekInfo.days;
    }
    return weekInfo.days.filter(day => !day.isWeekend);
  };

  const generateWardScheduleHTMLTable = (ward) => {
    const emailDays = getEmailDays();

    let html = `
      <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto;">
        <h2 style="color: #2c3e50; border-bottom: 2px solid #3498db; padding-bottom: 10px;">
          Weekly Ward Schedule - ${ward}
        </h2>
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin-bottom: 20px;">
          <table style="width: 100%; border-collapse: collapse;">
            <tr>
              <td style="padding: 5px;"><strong>Week:</strong> ${formatDateRange()}</td>
              <td style="padding: 5px;"><strong>Ward:</strong> ${ward}</td>
            </tr>
            <tr>
              <td style="padding: 5px;"><strong>Generated:</strong> ${new Date().toLocaleDateString()}</td>
              <td style="padding: 5px;"><strong>Generated By:</strong> ${userInfo?.name || 'Manager'}</td>
            </tr>
            <tr>
              <td style="padding: 5px;"><strong>Includes Weekend:</strong> ${includeWeekend ? 'Yes' : 'No'}</td>
              <td style="padding: 5px;"><strong>Days Shown:</strong> ${emailDays.length} of ${weekInfo.days.length}</td>
            </tr>
          </table>
        </div>
        <div style="overflow-x: auto;">
          <table style="width: 100%; border-collapse: collapse; border: 1px solid #ddd;">
            <thead>
              <tr style="background-color: #3498db; color: white;">
                <th style="padding: 12px; text-align: left; width: 15%;">Day</th>
                <th style="padding: 12px; text-align: left; width: 85%;">Shifts & Staff</th>
              </tr>
            </thead>
            <tbody>
    `;

    let hasShifts = false;

    emailDays.forEach((day, dayIndex) => {
      const shifts = wardShifts[ward]?.[day.date] || [];
      const dayHasShifts = shifts.length > 0;
      if (dayHasShifts) hasShifts = true;

      html += `
        <tr style="${dayIndex % 2 === 0 ? 'background-color: #f9f9f9;' : 'background-color: white;'}">
          <td style="padding: 10px; border: 1px solid #ddd; vertical-align: top;">
            <div style="font-weight: bold; color: ${day.isWeekend ? '#e67e22' : '#2c3e50'}">
              ${day.dayName}<br>
              <span style="font-size: 12px; color: #7f8c8d;">${day.date}</span>
            </div>
            ${day.isWeekend ? '<div style="font-size: 11px; color: #e67e22; font-style: italic;">Weekend</div>' : ''}
          </td>
          <td style="padding: 10px; border: 1px solid #ddd;">
      `;

      if (dayHasShifts) {
        shifts.forEach((shift) => {
          const shiftColor = shift.isFilled
            ? (shift.isAgency ? (isEveningShift(shift.startTime) ? '#6a0080' : '#c37ecf') : (shift.isWeekend ? '#e67e22' : '#2ecc71'))
            : (shift.isWeekend ? '#f39c12' : '#3498db');

          html += `
            <div style="background-color: ${shiftColor}10; border-left: 4px solid ${shiftColor}; padding: 8px; margin-bottom: 8px; border-radius: 4px;">
              <table style="width: 100%;">
                <tr>
                  <td style="width: 25%;"><strong style="color: ${shiftColor};">${shift.startTime} - ${shift.endTime}</strong></td>
                  <td style="width: 40%;">
                    <strong>Staff:</strong> ${shift.staffName}
                    ${shift.staffJobTitle && !shift.isAgency ? `<br><span style="font-size: 10px; opacity: 0.75;">${shift.staffJobTitle}</span>` : ''}
                    ${shift.isAgency ? `<br><span style="font-size: 11px; font-style: italic;">via ${shift.agencyName || 'Agency'}</span>` : ''}
                  </td>
                  <td style="width: 35%;"><strong>Status:</strong> ${shift.isFilled ? (shift.isAgency ? 'Agency' : 'Assigned') : 'Available'}</td>
                </tr>
                <tr>
                  <td colspan="3" style="padding-top: 4px;">
                    ${shift.location ? `<strong>Location:</strong> ${shift.location}<br>` : ''}
                    ${shift.isWeekend ? '<span style="color: #e67e22;">• Weekend Shift</span>' : ''}
                    ${!shift.isFilled ? '<span style="color: #3498db; margin-left: 10px;">• Shift Available</span>' : ''}
                    ${shift.isOvertime ? `<span style="background-color: #ff9800; color: white; font-weight: bold; padding: 2px 6px; border-radius: 4px; font-size: 10px; display: inline-block; margin-left: 10px;">OVERTIME</span>` : ''}
                  </td>
                </tr>
              </table>
            </div>
          `;
        });
      } else {
        html += `<div style="background-color: #f5f5f5; padding: 12px; text-align: center; color: #7f8c8d; font-style: italic; border-radius: 4px;">No shifts scheduled</div>`;
      }

      html += `</td></tr>`;
    });

    if (!hasShifts) {
      html += `
        <tr>
          <td colspan="2" style="padding: 30px; text-align: center; color: #7f8c8d;">
            <div style="font-size: 16px; margin-bottom: 10px;">No shifts scheduled for this week</div>
            <div style="font-size: 14px;">There are no shifts scheduled in ${ward} from ${formatDateRange()}</div>
          </td>
        </tr>
      `;
    }

    html += `
            </tbody>
          </table>
        </div>
        <div style="margin-top: 30px; padding: 15px; background-color: #f8f9fa; border-radius: 5px;">
          <h3 style="color: #2c3e50; margin-top: 0;">Ward Summary</h3>
          <ul style="color: #555;">
            <li>Total shifts this week: ${Object.values(wardShifts[ward] || {}).flat().length}</li>
            <li>Filled by staff: ${Object.values(wardShifts[ward] || {}).flat().filter(s => s.isFilled && !s.isAgency).length}</li>
            <li>Filled by agency: ${Object.values(wardShifts[ward] || {}).flat().filter(s => s.isFilled && s.isAgency).length}</li>
            <li>Available shifts: ${Object.values(wardShifts[ward] || {}).flat().filter(s => !s.isFilled).length}</li>
            <li>Weekend shifts: ${Object.values(wardShifts[ward] || {}).flat().filter(s => s.isWeekend).length}</li>
            <li>Days included: ${emailDays.length} (${includeWeekend ? 'Monday to Sunday' : 'Monday to Friday'})</li>
          </ul>
        </div>
        <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #7f8c8d; font-size: 12px;">
          <p>
            <strong>Beaumont Hospital Ward Scheduling System</strong><br>
            This schedule was automatically generated. Please do not reply to this email.<br>
            For questions, contact: beaumontNursingBank@beaumont.ie
          </p>
        </div>
      </div>
    `;

    return html;
  };

  const generateWardSchedulePlainText = (ward) => {
    const emailDays = getEmailDays();

    let text = `WARD WEEKLY SCHEDULE\n`;
    text += `====================\n\n`;
    text += `Ward: ${ward}\n`;
    text += `Week: ${formatDateRange()}\n`;
    text += `Generated: ${new Date().toLocaleDateString()}\n`;
    text += `Generated By: ${userInfo?.name || 'Manager'}\n`;
    text += `Includes Weekend: ${includeWeekend ? 'Yes' : 'No'}\n`;
    text += `Days Shown: ${emailDays.length} of ${weekInfo.days.length}\n\n`;
    text += `${'='.repeat(60)}\n\n`;

    let hasShifts = false;

    emailDays.forEach(day => {
      const shifts = wardShifts[ward]?.[day.date] || [];
      const dayHasShifts = shifts.length > 0;
      if (dayHasShifts) hasShifts = true;

      text += `${day.dayName.toUpperCase()} (${day.date})`;
      if (day.isWeekend) text += ' [WEEKEND]';
      text += '\n';
      text += '-'.repeat(40) + '\n';

      if (dayHasShifts) {
        shifts.forEach((shift, index) => {
          text += `${index + 1}. ${shift.startTime} - ${shift.endTime}\n`;
          text += `   Staff: ${shift.staffName}\n`;
          if (shift.staffJobTitle && !shift.isAgency) text += `   Job Title: ${shift.staffJobTitle}\n`;
          if (shift.isAgency) text += `   (via ${shift.agencyName || 'Agency'})\n`;
          text += `   Status: ${shift.isFilled ? (shift.isAgency ? 'Agency' : 'Assigned') : 'Available'}\n`;
          if (shift.location) text += `   Location: ${shift.location}\n`;
          if (shift.isWeekend) text += `   * Weekend Shift *\n`;
          text += '\n';
        });
      } else {
        text += `No shifts scheduled\n\n`;
      }
      text += '\n';
    });

    if (!hasShifts) {
      text += `\nNO SHIFTS SCHEDULED\n`;
      text += `There are no shifts scheduled in ${ward} from ${formatDateRange()}\n\n`;
    }

    const totalShifts = Object.values(wardShifts[ward] || {}).flat().length;
    const staffFilled = Object.values(wardShifts[ward] || {}).flat().filter(s => s.isFilled && !s.isAgency).length;
    const agencyFilled = Object.values(wardShifts[ward] || {}).flat().filter(s => s.isFilled && s.isAgency).length;
    const availableShifts = Object.values(wardShifts[ward] || {}).flat().filter(s => !s.isFilled).length;
    const weekendShifts = Object.values(wardShifts[ward] || {}).flat().filter(s => s.isWeekend).length;

    text += `\nWARD SUMMARY:\n`;
    text += `- Total shifts this week: ${totalShifts}\n`;
    text += `- Filled by staff: ${staffFilled}\n`;
    text += `- Filled by agency: ${agencyFilled}\n`;
    text += `- Available shifts: ${availableShifts}\n`;
    text += `- Weekend shifts: ${weekendShifts}\n`;
    text += `- Days included: ${emailDays.length} (${includeWeekend ? 'Monday to Sunday' : 'Monday to Friday'})\n\n`;

    text += `\nIMPORTANT NOTES:\n`;
    text += `- This schedule is subject to change based on operational needs\n`;
    text += `- Please contact ward manager for any schedule changes\n`;
    text += `- Weekend shifts are highlighted\n\n`;
    text += `Beaumont Hospital Ward Scheduling System\n`;
    text += `This schedule was automatically generated.\n`;
    text += `For questions, contact: beaumontNursingBank@beaumont.ie\n`;

    return text;
  };

  const openOutlookEmail = (ward, subject, body) => {
    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;

    setTimeout(() => {
      if (window.location.href.includes('mailto:')) {
        showSnackbar('Outlook not detected. Using web email instead.', 'info');
        setEmailMethod('web');
        setEmailDialogOpen(true);
      }
    }, 500);
  };

  const handleEmailWardSchedule = (ward) => {
    setSelectedWardForEmail({ name: ward });

    const defaultSubject = `${ward} Weekly Schedule - Week of ${formatDateRange()}`;
    const plainTextBody = generateWardSchedulePlainText(ward);

    setEmailSubject(defaultSubject);
    setEmailContent(plainTextBody);

    if (emailMethod === 'outlook') {
      try {
        openOutlookEmail(ward, defaultSubject, plainTextBody);
        showSnackbar('Opening Outlook...', 'info');
        return;
      } catch (error) {
        console.error('Failed to open Outlook:', error);
        showSnackbar('Could not open Outlook. Using web email.', 'warning');
        setEmailMethod('web');
      }
    }

    setEmailDialogOpen(true);
  };

  const handleSendWebEmail = async () => {
    if (!selectedWardForEmail) return;
    try {
      const htmlContent = generateWardScheduleHTMLTable(selectedWardForEmail.name);
      const plainTextContent = generateWardSchedulePlainText(selectedWardForEmail.name);

      const emailData = {
        to: '',
        subject: emailSubject,
        text: plainTextContent + '\n\n' + emailContent,
        html: htmlContent + '<hr><p><em>Additional Notes:</em></p><p>' + emailContent.replace(/\n/g, '<br>') + '</p>'
      };

      await new Promise(resolve => setTimeout(resolve, 1000));

      showSnackbar(`Ward schedule email prepared for ${selectedWardForEmail.name}`, 'success');
      setEmailDialogOpen(false);
    } catch (error) {
      console.error('Failed to send email:', error);
      showSnackbar('Failed to send email', 'error');
    }
  };

  const handleSendEmail = () => {
    if (emailMethod === 'outlook') {
      setEmailDialogOpen(false);
    } else {
      handleSendWebEmail();
    }
  };

  const handleCancelEmail = () => {
    setEmailDialogOpen(false);
    setSelectedWardForEmail(null);
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(selectedWeek);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setSelectedWeek(newDate);
  };

  const goToCurrentWeek = () => { setSelectedWeek(new Date()); };

  const handleDateChange = (event) => {
    const newDate = new Date(event.target.value);
    if (!isNaN(newDate.getTime())) {
      setSelectedWeek(newDate);
    }
  };

  const formatDateRange = () => {
    const start = new Date(weekInfo.startDate);
    const end = new Date(weekInfo.endDate);
    return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (loadingShifts || loadingStaff || loadingAvailabilities || loadingLocations || loadingAgencies) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Column widths for the ward info column and the day columns. All 7 days always stay
  // visible in the grid — the day filter narrows which ward ROWS show, not the columns.
  const wardColWidth = { xs: '140px', sm: '180px', md: '200px' };
  const dayColMinWidth = { xs: '100px', sm: '120px', md: '140px' };

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      <PrintStyles />

      <Breadcrumbs aria-label="breadcrumb" separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
        <Link underline="hover" color="inherit" href="#" onClick={(e) => { e.preventDefault(); navigate('/dashboard'); }}>
          Dashboard
        </Link>
        <Typography color="text.primary">Ward Weekly View</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ mb: 0, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            Ward Weekly Schedule
          </Typography>
          {isManager && managerDirectorate && (
            <Typography variant="subtitle1" color="primary" sx={{ mt: 1 }}>
              Directorate: {managerDirectorate}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            value={staffSource}
            exclusive
            onChange={(e, newValue) => newValue && setStaffSource(newValue)}
            size="small"
          >
            <ToggleButton value="internal"><People sx={{ mr: 1 }} />Staff</ToggleButton>
            <ToggleButton value="agency"><BusinessCenter sx={{ mr: 1 }} />Agency</ToggleButton>
          </ToggleButtonGroup>

          <Button
            variant="contained"
            startIcon={<Print />}
            onClick={handlePrint}
            sx={{ bgcolor: '#673ab7', '&:hover': { bgcolor: '#5e35b1' } }}
            className="no-print"
          >
            Print Schedule
          </Button>
        </Box>
      </Box>

      {/* Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }} className="no-print">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 2 }}>
          <IconButton onClick={() => navigateWeek(-1)}><ArrowBackIos /></IconButton>
          <Typography variant="h6" sx={{ fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
            Week of {formatDateRange()}
          </Typography>
          <IconButton onClick={() => navigateWeek(1)}><ArrowForwardIos /></IconButton>
          <Button startIcon={<Today />} onClick={goToCurrentWeek} variant="outlined" size="small">
            Current Week
          </Button>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarMonth color="action" />
          <TextField
            type="date"
            value={selectedWeek.toISOString().split('T')[0]}
            onChange={handleDateChange}
            size="small"
            label="Select Date"
            InputLabelProps={{ shrink: true }}
            sx={{ width: 200 }}
          />
        </Box>

        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Filter by Ward</InputLabel>
          <Select value={selectedWardFilter} label="Filter by Ward" onChange={(e) => setSelectedWardFilter(e.target.value)}>
            <MenuItem value="all">All Wards</MenuItem>
            {wards.map(ward => <MenuItem key={ward} value={ward}>{ward}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Filter by Reason</InputLabel>
          <Select value={selectedReasonFilter} label="Filter by Reason" onChange={(e) => setSelectedReasonFilter(e.target.value)}>
            <MenuItem value="all">All Reasons</MenuItem>
            {REASON_FOR_REQUEST.map(reason => (
              <MenuItem key={reason.value} value={reason.value}>{reason.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* NEW: Day filter now also narrows the shift grid to that single day, and — for the
            staff panel below — excludes anyone already assigned to a shift that day. */}
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Filter by Day</InputLabel>
          <Select value={selectedDayFilter} label="Filter by Day" onChange={(e) => setSelectedDayFilter(e.target.value)}>
            {dayOptions.map(day => (
              <MenuItem key={day} value={day}>{day === 'all' ? 'All Days' : day}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {staffSource === 'internal' && (
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Filter by Job Title</InputLabel>
            <Select value={selectedJobTitleFilter} label="Filter by Job Title" onChange={(e) => setSelectedJobTitleFilter(e.target.value)}>
              {jobTitles.map(title => (
                <MenuItem key={title} value={title}>{title === 'all' ? 'All Job Titles' : title}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {(selectedWardFilter !== 'all' || selectedJobTitleFilter !== 'all' || selectedDayFilter !== 'all' || selectedReasonFilter !== 'all') && (
          <Button variant="outlined" size="small" startIcon={<Clear />} onClick={clearAllFilters} sx={{ height: 40 }}>
            Clear Filters
          </Button>
        )}

        <Typography variant="body2" color="textSecondary">
          Showing {filteredWards.length} ward{filteredWards.length !== 1 ? 's' : ''}
          {staffSource === 'internal'
            ? ` • ${filteredStaffList.length} staff (${staffWithAvailabilitiesCount} available this week)`
            : ` • ${filteredAgenciesList.length} agencies (Tier1-4)`}
          {selectedDayFilter !== 'all' && ` • Showing only wards with a shift on ${selectedDayFilter}, and staff available that day`}
          {isManager && !isAdmin && managerDirectorate && ` • Directorate: ${managerDirectorate}`}
        </Typography>
      </Box>

      {getFilterSummary() !== 'No filters applied' && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={<Button size="small" color="inherit" onClick={clearAllFilters} startIcon={<Clear />}>Clear</Button>}
        >
          <Typography variant="body2">Filters applied: <strong>{getFilterSummary()}</strong></Typography>
        </Alert>
      )}

      {/* Mobile view switcher — on a phone the shift grid and the staff/agency panel each
          take the full width and stack, so a Schedule/Staff toggle avoids a huge amount of
          vertical scrolling before you can reach whichever one you need. */}
      {isMobile && (
        <Tabs
          value={mobileView}
          onChange={(e, v) => setMobileView(v)}
          variant="fullWidth"
          sx={{ mb: 2 }}
          className="no-print"
        >
          <Tab icon={<GridView fontSize="small" />} iconPosition="start" label="Schedule" value="shifts" />
          <Tab
            icon={<People fontSize="small" />}
            iconPosition="start"
            label={staffSource === 'internal' ? `Staff (${filteredStaffList.length})` : `Agencies (${filteredAgenciesList.length})`}
            value="staff"
          />
        </Tabs>
      )}

      <Grid container spacing={3}>
        {/* Main Ward Schedule */}
        {(!isMobile || mobileView === 'shifts') && (
        <Grid item xs={12} md={9}>
          <Paper elevation={3} sx={{ overflow: 'hidden', width: '100%' }} className="print-page">
            <Box sx={{ width: '100%', overflow: 'hidden' }}>
              {/* Fixed Header Row */}
              <Grid container sx={fixedHeaderStyle}>
                <Grid item sx={{ width: wardColWidth }}>
                  <Box sx={{ p: { xs: 1, sm: 2 }, borderRight: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'primary.main', color: 'white', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Typography fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>Ward</Typography>
                    <Typography variant="caption" sx={{ display: 'block', opacity: 0.8, fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                      {filteredWards.length} wards
                    </Typography>
                  </Box>
                </Grid>

                {weekInfo.days.map(day => (
                  <Grid item key={day.date} sx={{ flex: 1, minWidth: dayColMinWidth }}>
                    <Box sx={{ p: { xs: 1, sm: 2 }, borderBottom: 1, borderColor: 'divider', bgcolor: day.isWeekend ? 'secondary.main' : 'primary.light', color: 'white', textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography fontWeight="bold" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>{day.shortName}</Typography>
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                        {new Date(day.date).getDate()}/{new Date(day.date).getMonth() + 1}
                      </Typography>
                      {day.isWeekend && (
                        <Typography variant="caption" sx={{ fontStyle: 'italic', fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>Weekend</Typography>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Scrollable Container for Ward Rows */}
              <Box sx={scrollableTableStyle}>
                {filteredWards.map((ward, index) => (
                  <Grid container key={ward} className="ward-row print-page" sx={{ width: '100%' }}>
                    <Grid item sx={{ width: wardColWidth }}>
                      <Box sx={{ p: { xs: 1, sm: 2 }, borderRight: 1, borderBottom: 1, borderColor: 'divider', bgcolor: index % 2 === 0 ? 'grey.50' : 'white', minHeight: { xs: '120px', sm: '150px' }, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                        <Box>
                          <Typography fontWeight="bold" variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>{ward}</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                            {wardShifts[ward] ? Object.values(wardShifts[ward]).flat().length + ' shift(s)' : 'No shifts'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                            Filled: {Object.values(wardShifts[ward] || {}).flat().filter(s => s.isFilled).length}
                            {Object.values(wardShifts[ward] || {}).flat().filter(s => s.isAgency).length > 0 &&
                              ` (Agency: ${Object.values(wardShifts[ward] || {}).flat().filter(s => s.isAgency).length})`}
                            {' • '}
                            Available: {Object.values(wardShifts[ward] || {}).flat().filter(s => !s.isFilled).length}
                          </Typography>
                        </Box>
                        <Tooltip title={`Email schedule for ${ward}`}>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<Email />}
                            onClick={() => handleEmailWardSchedule(ward)}
                            sx={{ mt: 1, bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' }, fontSize: { xs: '0.6rem', sm: '0.7rem' }, minWidth: { xs: 'auto', sm: '100px' } }}
                            className="no-print"
                          >
                            Email
                          </Button>
                        </Tooltip>
                      </Box>
                    </Grid>

                    {weekInfo.days.map(day => (
                      <Grid item key={`${ward}-${day.date}`} sx={{ flex: 1, minWidth: dayColMinWidth }}>
                        <Box
                          sx={{ p: { xs: 0.5, sm: 1 }, minHeight: { xs: '120px', sm: '150px' }, borderRight: 1, borderBottom: 1, borderColor: 'divider', bgcolor: day.isWeekend ? 'grey.100' : (index % 2 === 0 ? 'grey.50' : 'white'), cursor: 'pointer', overflow: 'hidden', height: '100%' }}
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, { ward, date: day.date, ...wardShifts[ward]?.[day.date]?.[0] })}
                          className="print-page"
                        >
                          {wardShifts[ward]?.[day.date]?.map((shift, idx) => {
                            const handleCardClick = (e) => {
                              if (e.detail === 2 && shift.isFilled) {
                                handleShiftDoubleClick(shift);
                              }
                            };
                            const handleEditIconClick = (e) => {
                              e.stopPropagation();
                              handleEditShiftClick(shift);
                            };
                            const categoryOfStaff = shift.categoryOfStaff || shift.shiftData?.categoryOfStaff || '';

                            return (
                              <Tooltip
                                key={`${shift.id}-${idx}`}
                                title={
                                  <div style={{ maxWidth: '200px' }}>
                                    <div><strong>Time:</strong> {shift.startTime} - {shift.endTime}</div>
                                    <div><strong>Status:</strong> {shift.agencyCancelled ? 'Agency Cancelled' : (shift.isFilled ? (shift.isAgency ? 'Agency' : 'Assigned') : 'Available')}</div>
                                    {shift.isOvertime && <div><strong style={{ color: '#ff9800' }}>⭐ Overtime Shift</strong></div>}
                                    {shift.isFilled ? (
                                      <>
                                        <div><strong>Staff:</strong> {shift.staffName}</div>
                                        {shift.staffJobTitle && !shift.isAgency && (<div><strong>Job Title:</strong> {shift.staffJobTitle}</div>)}
                                        {shift.isAgency && (
                                          <>
                                            <div><strong>Agency:</strong> {shift.agencyName}</div>
                                            <div><strong>Contact:</strong> {shift.agencyWorkerContact || 'Not provided'}</div>
                                            {shift.poNumber && <div><strong>PO Number:</strong> {shift.poNumber}</div>}
                                            {shift.agencyCancelled && <div><strong style={{ color: '#ffcc80' }}>⚠️ Marked Cancelled</strong></div>}
                                            {shift.actualStatus === 'Not-Worked' && shift.supersededByShiftId && (
                                              <div><strong style={{ color: '#ffb300' }}>⚠️ Replaced</strong> — actual filler changed{shift.supersededByPoNumber ? `, new PO: ${shift.supersededByPoNumber}` : ''}</div>
                                            )}
                                          </>
                                        )}
                                      </>
                                    ) : (
                                      <div><strong>Available Shift</strong></div>
                                    )}
                                  </div>
                                }
                                arrow
                                className="no-print"
                              >
                                <Box sx={{ mb: 0.5, cursor: 'pointer', fontSize: { xs: '0.6rem', sm: '0.7rem' } }}
                                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                  onDrop={(e) => { e.stopPropagation(); handleDrop(e, shift); }}
                                  onClick={handleCardClick}
                                  className="no-print"
                                >
                                  <Card sx={{
                                    bgcolor: shift.agencyCancelled ? '#e65100' : shift.isFilled
                                      ? (shift.isAgency ? (isEveningShift(shift.startTime) ? '#6a0080' : '#9c27b0') : (isEveningShift(shift.startTime) ? '#2e7d32' : '#66bb6a'))
                                      : (isEveningShift(shift.startTime) ? '#1565c0' : '#64b5f6'),
                                    color: 'white',
                                    '&:hover': { boxShadow: shift.isFilled ? 4 : 2, transform: shift.isFilled ? 'translateY(-2px)' : 'translateY(-1px)', transition: 'all 0.2s' },
                                    position: 'relative'
                                  }}>
                                    <IconButton
                                      size="small"
                                      sx={{ position: 'absolute', top: { xs: 0, sm: 2 }, right: { xs: 0, sm: 2 }, color: 'white', backgroundColor: 'rgba(0,0,0,0.2)', padding: { xs: '1px', sm: '2px' }, '&:hover': { backgroundColor: 'rgba(0,0,0,0.4)' } }}
                                      onClick={handleEditIconClick}
                                      className="no-print"
                                    >
                                      <Edit fontSize="small" sx={{ fontSize: { xs: '0.7rem', sm: '0.9rem' } }} />
                                    </IconButton>

                                    <CardContent sx={{ p: { xs: 0.5, sm: 1 }, pr: { xs: 2, sm: 3 } }}>
                                      <Typography variant="body2" sx={{ fontSize: { xs: '0.6rem', sm: '0.8rem' } }}>{shift.startTime} - {shift.endTime}</Typography>
                                      <Typography variant="body2" sx={{ fontSize: { xs: '0.6rem', sm: '0.8rem' }, fontWeight: 'bold' }}>
                                        {shift.staffName.length > 15 ? shift.staffName.substring(0, 12) + '...' : shift.staffName}
                                      </Typography>

                                      {shift.staffJobTitle && !shift.isAgency && (
                                        <Chip label={shift.staffJobTitle} size="small" sx={{ height: { xs: 12, sm: 14 }, fontSize: { xs: '0.45rem', sm: '0.5rem' }, bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 'bold', mt: 0.25, '& .MuiChip-label': { px: 0.5 } }} />
                                      )}

                                      {shift.agencyCancelled && (
                                        <Chip label={shift.chargeApplies ? 'CANCELLED (Charge)' : 'CANCELLED'} size="small" sx={{ height: { xs: 16, sm: 20 }, fontSize: { xs: '0.5rem', sm: '0.6rem' }, bgcolor: '#fbc02d', color: 'black', fontWeight: 'bold', mt: 0.25 }} />
                                      )}

                                      {shift.actualStatus === 'Not-Worked' && shift.supersededByShiftId && (
                                        <Chip
                                          label="Replaced"
                                          size="small"
                                          onClick={(e) => { e.stopPropagation(); navigate(`/${userInfo.isAdmin ? 'admin' : 'manager'}/shiftsWard/${shift.supersededByShiftId}/edit`); }}
                                          sx={{ height: { xs: 16, sm: 20 }, fontSize: { xs: '0.5rem', sm: '0.6rem' }, bgcolor: '#ffb300', color: 'black', fontWeight: 'bold', mt: 0.25, cursor: 'pointer', '&:hover': { bgcolor: '#ffa000' } }}
                                        />
                                      )}

                                      {shift.isAgency && (
                                        <Typography variant="caption" sx={{ fontSize: { xs: '0.5rem', sm: '0.65rem' }, fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                                          via {shift.agencyName?.substring(0, 10)}...
                                        </Typography>
                                      )}

                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, width: '100%' }}>
                                        {categoryOfStaff && (
                                          <Typography variant="caption" sx={{ fontSize: { xs: '0.5rem', sm: '0.6rem' }, opacity: 0.9, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                                            {categoryOfStaff.length > 10 ? categoryOfStaff.substring(0, 8) + '...' : categoryOfStaff}
                                          </Typography>
                                        )}
                                        {shift.isOvertime && (
                                          <Chip label="Overtime" size="small" sx={{ height: { xs: 16, sm: 20 }, fontSize: { xs: '0.5rem', sm: '0.6rem' }, bgcolor: 'warning.main', color: 'white', fontWeight: 'bold', flexShrink: 0 }} />
                                        )}
                                        {isShiftApproved(shift) && (
                                          <Chip label="Approved" size="small" sx={{ height: { xs: 16, sm: 20 }, fontSize: { xs: '0.5rem', sm: '0.6rem' }, bgcolor: '#09310b', color: 'white', fontWeight: 'bold', flexShrink: 0 }} />
                                        )}
                                        {!isShiftApproved(shift) && isShiftPendingApproval(shift) && (
                                          <Chip label="Not Approved Yet" size="small" sx={{ height: { xs: 16, sm: 20 }, fontSize: { xs: '0.5rem', sm: '0.6rem' }, bgcolor: '#b71c1c', color: 'white', fontWeight: 'bold', flexShrink: 0 }} />
                                        )}
                                      </Box>

                                      {!shift.isFilled && (
                                        <Typography variant="caption" sx={{ fontSize: { xs: '0.5rem', sm: '0.65rem' }, display: 'block', fontStyle: 'italic', mt: 0.5 }} className="no-print">
                                          Drag to assign
                                        </Typography>
                                      )}

                                      {shift.isFilled && (
                                        <Typography variant="caption" sx={{ fontSize: { xs: '0.45rem', sm: '0.55rem' }, display: 'block', opacity: 0.8, mt: 0.25 }} className="no-print">
                                          Double-click to unassign
                                        </Typography>
                                      )}
                                    </CardContent>
                                  </Card>
                                </Box>
                              </Tooltip>
                            );
                          })}

                          {(!wardShifts[ward]?.[day.date] || wardShifts[ward][day.date].length === 0) && (
                            <Box
                              sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', fontStyle: 'italic', fontSize: { xs: '0.6rem', sm: '0.8rem' }, textAlign: 'center', p: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover', border: '1px dashed', borderColor: 'primary.main' } }}
                              onClick={(e) => { if (e.detail === 2) { handleCreateShiftClick(ward, day); } }}
                              className="no-print"
                            >
                              Double-click<br />to create
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                ))}

                {filteredWards.length === 0 && (
                  <Grid container sx={{ width: '100%' }}>
                    <Grid item xs={12}>
                      <Box sx={{ p: 4, textAlign: 'center', borderBottom: 1, borderColor: 'divider' }}>
                        <Typography variant="body1" color="textSecondary">
                          {isManager && managerDirectorate ? `No wards found for ${managerDirectorate} in the selected week` : 'No wards found for the selected week'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>
        )}

        {/* Staff/Agency Panel */}
        {(!isMobile || mobileView === 'staff') && (
        <Grid item xs={12} md={3} className="no-print">
          <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
            {staffSource === 'internal' ? (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Available Staff ({filteredStaffList.length})
                  </Typography>

                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by name or job title..."
                    value={staffSearchQuery}
                    onChange={(e) => setStaffSearchQuery(e.target.value)}
                    sx={{ mb: 1.5 }}
                    InputProps={{
                      endAdornment: staffSearchQuery && (
                        <IconButton size="small" onClick={() => setStaffSearchQuery('')} edge="end">
                          <Clear fontSize="small" />
                        </IconButton>
                      ),
                    }}
                  />

                  {filteredStaffList.some(staff => {
                    const availabilities = getStaffAvailabilities(staff._id);
                    return availabilities.some(a => a.isSickLeave);
                  }) && (
                    <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>
                      <Typography variant="caption">⚠️ Some staff have sick leave marked this week (shown in red)</Typography>
                    </Alert>
                  )}

                  <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                    {selectedJobTitleFilter !== 'all' && (
                      <Chip label={`Job: ${selectedJobTitleFilter}`} size="small" color="primary" onDelete={() => setSelectedJobTitleFilter('all')} />
                    )}
                    {selectedDayFilter !== 'all' && (
                      <Chip label={`Day: ${selectedDayFilter}`} size="small" color="secondary" onDelete={() => setSelectedDayFilter('all')} />
                    )}
                  </Box>

                  <Typography variant="body2" color="textSecondary" paragraph>
                    Drag to assign to shifts • {staffWithAvailabilitiesCount} staff available this week
                    {selectedDayFilter !== 'all' && ` (filtered to ${selectedDayFilter}, excluding staff already assigned that day)`}
                  </Typography>
                </Box>

                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    {selectedDayFilter === 'all'
                      ? 'Staff are sorted with those available this week first, then alphabetically'
                      : `Showing only staff available on ${selectedDayFilter} who are not already assigned to a shift that day`}
                  </Typography>
                </Alert>

                <Box sx={{ maxHeight: { xs: 'none', md: '600px' }, overflow: 'auto' }}>
                  {filteredStaffList.length === 0 ? (
                    <Alert severity={staffList.length === 0 ? "info" : "warning"}>
                      {staffList.length === 0
                        ? "No staff available"
                        : staffSearchQuery.trim()
                          ? `No staff match "${staffSearchQuery.trim()}"`
                          : `No staff match the selected ${selectedJobTitleFilter !== 'all' ? 'job title ' : ''}${selectedDayFilter !== 'all' ? 'day ' : ''}filter`}
                      {(selectedJobTitleFilter !== 'all' || selectedDayFilter !== 'all' || staffSearchQuery.trim()) && (
                        <Button size="small" onClick={() => { clearAllFilters(); setStaffSearchQuery(''); }} sx={{ ml: 1 }}>
                          Clear Filters
                        </Button>
                      )}
                    </Alert>
                  ) : (
                    <>
                      {staffWithAvailabilitiesCount > 0 && staffWithAvailabilitiesCount < filteredStaffList.length && (
                        <Box sx={{ mb: 2 }}>
                          <Divider sx={{ mb: 1 }}>
                            <Chip label={`${staffWithAvailabilitiesCount} with availability`} size="small" color="primary" variant="outlined" />
                          </Divider>
                        </Box>
                      )}

                      {filteredStaffList.map((staff) => {
                        const staffAvailabilities = getStaffAvailabilities(staff._id);
                        const hasAvailability = staffAvailabilities.length > 0;

                        return (
                          <Card
                            key={staff._id}
                            draggable={true}
                            onDragStart={(e) => handleDragStart(e, staff)}
                            sx={{
                              mb: 2, cursor: 'grab',
                              borderLeft: hasAvailability ? '4px solid #4caf50' : '4px solid transparent',
                              borderTop: staffAvailabilities.some(a => a.isUnavailable || a.isSickLeave) ? '2px solid #f44336' : 'none',
                              opacity: staffAvailabilities.some(a => a.isUnavailable || a.isSickLeave) ? 0.7 : 1,
                              '&:hover': { transform: 'translateY(-2px)', transition: 'transform 0.2s', boxShadow: 3 }
                            }}
                            className="no-print"
                          >
                            <CardContent>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="subtitle2" fontWeight="bold">{staff.name}</Typography>
                                  <Typography variant="body2">{staff.jobtitle}</Typography>
                                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>{staff.email}</Typography>
                                </Box>

                                {hasAvailability && (
                                  <Tooltip title={`${staffAvailabilities.length} availability${staffAvailabilities.length !== 1 ? 's' : ''} this week`}>
                                    <Chip icon={<EventAvailable fontSize="small" />} label={`${staffAvailabilities.length}`} size="small" color="primary" variant="outlined" sx={{ ml: 1 }} />
                                  </Tooltip>
                                )}

                                {staffAvailabilities.some(a => a.isUnavailable || a.isSickLeave) && (
                                  <Tooltip title="Staff has unavailability marked this week">
                                    <Chip label="Unavailable" size="small" color="error" variant="outlined" sx={{ fontSize: '0.6rem' }} />
                                  </Tooltip>
                                )}

                                {staffAvailabilities.some(a => a.isSickLeave) && (
                                  <Tooltip title="Staff has sick leave marked this week">
                                    <Chip label="Sick Leave" size="small" color="error" variant="outlined" sx={{ fontSize: '0.6rem' }} />
                                  </Tooltip>
                                )}

                                {staff.wardExclusions?.length > 0 && (
                                  <Tooltip title={`Excluded from: ${staff.wardExclusions.join(', ')}`}>
                                    <Chip label={`${staff.wardExclusions.length} excl.`} size="small" sx={{ bgcolor: '#d32f2f', color: 'white', fontSize: '0.6rem' }} />
                                  </Tooltip>
                                )}
                              </Box>

                              <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                <Chip label={staff.speciality || 'General'} size="small" variant="outlined" />
                                {staff.isLocum && <Chip label="Locum" size="small" color="warning" variant="outlined" />}
                                {staff.isBank && <Chip label="Bank" size="small" color="info" variant="outlined" />}
                              </Box>

                              {staff.wardExclusions?.length > 0 && (
                                <Box sx={{ mt: 1, p: 0.75, bgcolor: '#ffebee', borderRadius: 1, border: '1px solid #ef9a9a' }}>
                                  <Typography variant="caption" sx={{ color: '#c62828', fontWeight: 'bold', display: 'block', mb: 0.25 }}>⛔ Cannot be assigned to:</Typography>
                                  <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap' }}>
                                    {staff.wardExclusions.map(ward => (
                                      <Chip key={ward} label={ward} size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: '#d32f2f', color: 'white' }} />
                                    ))}
                                  </Box>
                                </Box>
                              )}

                              {hasAvailability && (
                                <Box sx={{ mt: 2 }} className="availability-section">
                                  <Divider sx={{ mb: 1 }} />
                                  <Typography variant="caption" fontWeight="bold" color="primary" sx={{ display: 'block', mb: 0.5 }}>Available This Week:</Typography>
                                  <Box sx={{ maxHeight: '150px', overflow: 'auto' }}>
                                    {staffAvailabilities.map((availability, index) => (
                                      <Box
                                        key={availability._id || index}
                                        sx={{ mb: 0.5, p: 0.5, bgcolor: (availability.isUnavailable || availability.isSickLeave) ? 'error.50' : 'primary.50', borderRadius: '4px', borderLeft: '3px solid', borderColor: (availability.isUnavailable || availability.isSickLeave) ? 'error.main' : 'primary.main' }}
                                      >
                                        <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block' }}>
                                          {formatAvailabilityDisplay(availability)}
                                          {(availability.isUnavailable || availability.isSickLeave) && (
                                            <Chip label="Unavailable" size="small" sx={{ ml: 1, height: 14, fontSize: '0.55rem', bgcolor: 'error.main', color: 'white' }} />
                                          )}
                                        </Typography>
                                        {availability.comments && (
                                          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', fontStyle: 'italic', display: 'block' }}>
                                            {availability.comments}
                                          </Typography>
                                        )}
                                        {(availability.isUnavailable || availability.isSickLeave) && (availability.unavailabilityReason || availability.sicknessReason) && (
                                          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'error.main', fontStyle: 'italic', display: 'block' }}>
                                            Reason: {availability.unavailabilityReason || availability.sicknessReason}
                                          </Typography>
                                        )}
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>
                              )}

                              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                Drag to assign
                              </Typography>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </>
                  )}
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom>Available Agencies ({filteredAgenciesList.length})</Typography>

                  <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                    {['Tier1', 'Tier2', 'Tier3', 'Tier4'].map(tier => {
                      const count = filteredAgenciesList.filter(a => a.agencyTier === tier).length;
                      if (count === 0) return null;
                      return (
                        <Chip key={tier} label={`${tier}: ${count}`} size="small" sx={{ bgcolor: TIER_COLORS[tier], color: 'white', fontWeight: 'bold' }} />
                      );
                    })}
                  </Box>

                  <Typography variant="body2" color="textSecondary" paragraph>
                    Drag agency to assign to shifts • Sorted by Tier (Tier1 to Tier4)
                  </Typography>

                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">When you drag an agency to a shift, you'll be prompted to enter the worker's name and contact details.</Typography>
                  </Alert>
                </Box>

                <Box sx={{ maxHeight: { xs: 'none', md: '600px' }, overflow: 'auto' }}>
                  {filteredAgenciesList.length === 0 ? (
                    <Alert severity="info">No approved agencies available</Alert>
                  ) : (
                    <>
                      {['Tier1', 'Tier2', 'Tier3', 'Tier4'].map(tier => {
                        const tierAgencies = filteredAgenciesList.filter(a => a.agencyTier === tier);
                        if (tierAgencies.length === 0) return null;
                        return (
                          <Box key={tier} sx={{ mb: 3 }}>
                            <Divider sx={{ mb: 1 }}>
                              <Chip label={tier} size="small" sx={{ bgcolor: TIER_COLORS[tier], color: 'white', fontWeight: 'bold' }} />
                            </Divider>
                            {tierAgencies.map((agency) => (
                              <Card
                                key={agency._id}
                                draggable={true}
                                onDragStart={(e) => handleAgencyDragStart(e, agency)}
                                sx={{ mb: 2, cursor: 'grab', borderLeft: `4px solid ${TIER_COLORS[agency.agencyTier]}`, '&:hover': { transform: 'translateY(-2px)', transition: 'transform 0.2s', boxShadow: 3 } }}
                                className="no-print"
                              >
                                <CardContent>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="subtitle2" fontWeight="bold">{agency.agencyName}</Typography>
                                      <Typography variant="body2" color="textSecondary">{agency.agencyTier}</Typography>
                                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>{agency.agencyEmail}</Typography>
                                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>{agency.agencyPhone}</Typography>
                                    </Box>
                                  </Box>

                                  <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                    <Chip label={agency.agencyAddress ? 'Has Address' : 'No Address'} size="small" variant="outlined" color={agency.agencyAddress ? 'success' : 'default'} />
                                    <Chip label={`Days to shift: ${agency.daysToShiftStart}`} size="small" variant="outlined" />
                                  </Box>

                                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>Drag to assign</Typography>
                                </CardContent>
                              </Card>
                            ))}
                          </Box>
                        );
                      })}
                    </>
                  )}
                </Box>
              </>
            )}
          </Paper>
        </Grid>
        )}
      </Grid>

      {/* Agency Worker Details Dialog */}
      <Dialog open={agencyWorkerDialogOpen} onClose={() => setAgencyWorkerDialogOpen(false)} maxWidth="sm" fullWidth className="no-print">
        <DialogTitle>Enter Agency Worker Details</DialogTitle>
        <DialogContent>
          {selectedAgency && selectedShiftForAgency && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Agency:</strong> {selectedAgency.agencyName}<br />
                  <strong>Shift:</strong> {selectedShiftForAgency.startTime} - {selectedShiftForAgency.endTime}<br />
                  <strong>Date:</strong> {new Date(selectedShiftForAgency.shiftData.startDateTime).toLocaleDateString()}
                </Typography>
              </Alert>

              <TextField fullWidth label="Worker Name *" value={agencyWorkerDetails.workerName} onChange={(e) => setAgencyWorkerDetails({ ...agencyWorkerDetails, workerName: e.target.value })} margin="normal" required helperText="Enter the name of the agency worker" />
              <TextField fullWidth label="Contact Number *" value={agencyWorkerDetails.workerContact} onChange={(e) => setAgencyWorkerDetails({ ...agencyWorkerDetails, workerContact: e.target.value })} margin="normal" required helperText="Enter contact number for the worker" />
              <TextField fullWidth label="Contact Email (Optional)" value={agencyWorkerDetails.workerEmail} onChange={(e) => setAgencyWorkerDetails({ ...agencyWorkerDetails, workerEmail: e.target.value })} margin="normal" type="email" helperText="Worker's email (if different from agency email)" />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAgencyWorkerDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAgencyAssignment} variant="contained" color="primary" disabled={!agencyWorkerDetails.workerName || !agencyWorkerDetails.workerContact}>
            Assign Agency Worker
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create New Shift Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="lg" fullWidth className="no-print">
        <DialogTitle>Create New Shift</DialogTitle>
        <DialogContent>
          {newShiftInfo.ward && (
            <Box sx={{ mt: 1 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Creating new shift for: <strong>{newShiftInfo.ward}</strong><br />
                  Date: <strong>{newShiftInfo.dayInfo?.dayName}, {new Date(newShiftInfo.dayDate).toLocaleDateString()}</strong>
                </Typography>
              </Alert>

              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 2 }}>
                  <Tab label="Basic Information" value="basic" />
                  <Tab label="Schedule" value="schedule" />
                </Tabs>
              </Box>

              {activeTab === 'basic' && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Hospital Location</InputLabel>
                      <Select name="hospitalLocationId" value={editForm.hospitalLocationId || ''} label="Hospital Location" onChange={handleEditFormChange} size="small" required>
                        <MenuItem value="">Select Hospital Location...</MenuItem>
                        {filteredHospitalLocations?.map(location => (
                          <MenuItem key={location._id} value={location._id}>{location.wardDepartmentArea}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Category of Staff</InputLabel>
                      <Select name="categoryOfStaff" value={editForm.categoryOfStaff || ''} label="Category of Staff" onChange={handleEditFormChange} size="small" required>
                        <MenuItem value="">Select Category...</MenuItem>
                        {CATEGORY_OF_STAFF.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Reason for Request</InputLabel>
                      <Select name="reasonForRequest" value={editForm.reasonForRequest || ''} label="Reason for Request" onChange={handleEditFormChange} size="small" required>
                        <MenuItem value="">Select Reason...</MenuItem>
                        {REASON_FOR_REQUEST.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>

                  {['1-1 special', 'Cohorts'].some(r => editForm.reasonForRequest?.toLowerCase() === r.toLowerCase()) && (
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth margin="normal" required>
                        <InputLabel>Specials / Cohorts Reason</InputLabel>
                        <Select name="specialsReason" value={editForm.specialsReason || ''} label="Specials / Cohorts Reason" onChange={handleEditFormChange} size="small">
                          <MenuItem value="">Select reason...</MenuItem>
                          {SPECIALS_REASON.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <TextField fullWidth label="Reason for Request Details" name="reasonForRequestDetails" value={editForm.reasonForRequestDetails || ''} onChange={handleEditFormChange} margin="normal" multiline rows={3} placeholder="Provide details about why this shift is needed..." required />
                  </Grid>

                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8f9fa' }}>
                      <Typography variant="subtitle2" gutterBottom>Options</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={4}>
                          <FormControlLabel control={<Checkbox checked={editForm.escort || false} onChange={(e) => setEditForm(prev => ({ ...prev, escort: e.target.checked }))} name="escort" />} label="Escort Required" />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <FormControlLabel control={<Checkbox checked={editForm.special || false} onChange={(e) => setEditForm(prev => ({ ...prev, special: e.target.checked }))} name="special" />} label="Is this a Special" />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <FormControlLabel control={<Checkbox checked={editForm.isOvertime || false} onChange={(e) => setEditForm(prev => ({ ...prev, isOvertime: e.target.checked }))} name="isOvertime" />} label="Overtime Shift" />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={editForm.isActive !== false}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  if (!checked && shiftToEdit?.isAgency) {
                                    const shiftObj = wardShifts[shiftToEdit.ward]?.[shiftToEdit.dayDate]?.find(s => s.id === shiftToEdit.shiftId);
                                    setEditDialogOpen(false);
                                    if (shiftObj) openCancelDialogForShift(shiftObj);
                                    return;
                                  }
                                  setEditForm(prev => ({ ...prev, isActive: checked }));
                                }}
                                name="isActive"
                              />
                            }
                            label="Active Shift"
                          />
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', ml: 4 }}>Uncheck to create an inactive shift</Typography>
                        </Grid>
                        <Grid item xs={12} sm={8}>
                          <TextField fullWidth label="ECC Approval Number" name="eccNumber" value={editForm.eccNumber || ''} onChange={handleEditFormChange} size="small" placeholder="Enter ECC number..." />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Special Requirements" name="specialRequirements" value={editForm.specialRequirements || ''} onChange={handleEditFormChange} margin="normal" multiline rows={4} placeholder="Any special equipment, training, or requirements..." />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Comments" name="comments" value={editForm.comments || ''} onChange={handleEditFormChange} margin="normal" multiline rows={4} placeholder="Additional notes or comments..." />
                  </Grid>
                </Grid>
              )}

              {activeTab === 'schedule' && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Shift Time Preset</Typography>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Grid container spacing={1}>
                        {Object.entries(SHIFT_PRESETS).map(([key, preset]) => (
                          <Grid item xs={6} md={4} key={key}>
                            <Paper elevation={editForm.selectedPreset === key ? 2 : 0} sx={{ p: 1, cursor: 'pointer', bgcolor: editForm.selectedPreset === key ? 'primary.main' : 'grey.100', color: editForm.selectedPreset === key ? 'white' : 'inherit', '&:hover': { bgcolor: editForm.selectedPreset === key ? 'primary.dark' : 'grey.200' } }} onClick={() => handlePresetSelect(key)}>
                              <Typography variant="body2">{preset.label}</Typography>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </Paper>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <TextField fullWidth label="Start Date" type="date" name="startDate" value={editForm.startDate || ''} onChange={handleEditFormChange} InputLabelProps={{ shrink: true }} margin="normal" size="small" required />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField fullWidth label="End Date (Same Day)" type="date" name="endDate" value={editForm.endDate || ''} onChange={handleEditFormChange} InputLabelProps={{ shrink: true }} margin="normal" size="small" helperText="Same day unless overnight shift" required />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField fullWidth label="Start Time" type="time" name="startTime" value={editForm.startTime || ''} onChange={handleEditFormChange} InputLabelProps={{ shrink: true }} margin="normal" size="small" required />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField fullWidth label="End Time" type="time" name="endTime" value={editForm.endTime || ''} onChange={handleEditFormChange} InputLabelProps={{ shrink: true }} margin="normal" size="small" required />
                  </Grid>

                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ mt: 1 }}>
                      <Typography variant="body2"><strong>Shift Duration:</strong> {calculateShiftDuration()}</Typography>
                    </Alert>
                  </Grid>
                </Grid>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateShift}
            variant="contained"
            color="primary"
            startIcon={<Edit />}
            disabled={
              !editForm.hospitalLocationId || !editForm.categoryOfStaff || !editForm.reasonForRequest ||
              (['1-1 special', 'Cohorts'].some(r => editForm.reasonForRequest?.toLowerCase() === r.toLowerCase()) && !editForm.specialsReason)
            }
          >
            Create Shift
          </Button>
        </DialogActions>
      </Dialog>

      {/* Release Shift Confirmation Dialog */}
      <Dialog open={releaseDialogOpen} onClose={handleCancelRelease} maxWidth="sm" fullWidth className="no-print">
        <DialogTitle>{shiftToRelease?.isAgency ? 'Cancel Agency Shift' : 'Unassign from Shift'}</DialogTitle>
        <DialogContent>
          {shiftToRelease?.isAgency ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This shift has already been sent to the agency{shiftToRelease.poNumber ? ` with PO ${shiftToRelease.poNumber}` : ''}. It cannot simply be reopened — marking it cancelled here keeps the agency and PO details on record. You'll still need to contact the agency directly to cancel the actual booking.
            </Alert>
          ) : (
            <Typography variant="body1" paragraph>Are you sure you want to unassign this staff member from the shift?</Typography>
          )}

          {shiftToRelease && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2"><strong>{shiftToRelease.isAgency ? 'Worker:' : 'Staff:'}</strong> {shiftToRelease.staffName}</Typography>
              {shiftToRelease.isAgency && shiftToRelease.agencyName && (<Typography variant="body2"><strong>Agency:</strong> {shiftToRelease.agencyName}</Typography>)}
              {shiftToRelease.isAgency && shiftToRelease.poNumber && (<Typography variant="body2"><strong>PO Number:</strong> {shiftToRelease.poNumber}</Typography>)}
              <Typography variant="body2"><strong>Ward:</strong> {shiftToRelease.ward}</Typography>
              <Typography variant="body2"><strong>Time:</strong> {shiftToRelease.shiftTime}</Typography>
              <Typography variant="body2"><strong>Date:</strong> {new Date(shiftToRelease.dayDate).toLocaleDateString()}</Typography>
            </Box>
          )}

          {shiftToRelease?.isAgency && (
            <Box sx={{ mt: 3 }}>
              <FormControl component="fieldset">
                <Typography variant="subtitle2" gutterBottom>Who is cancelling this shift? *</Typography>
                <RadioGroup row value={cancelledByType} onChange={(e) => setCancelledByType(e.target.value)}>
                  <FormControlLabel value="Agency" control={<Radio />} label="Agency" />
                  <FormControlLabel value="Beaumont" control={<Radio />} label="Beaumont" />
                </RadioGroup>
              </FormControl>

              {cancelledByType === 'Beaumont' && (
                <Box sx={{ mt: 2 }}>
                  <FormControl component="fieldset">
                    <Typography variant="subtitle2" gutterBottom>Was the agency given at least 12 hours notice of this cancellation? *</Typography>
                    <RadioGroup row value={advanceNoticeGiven === null ? '' : String(advanceNoticeGiven)} onChange={(e) => setAdvanceNoticeGiven(e.target.value === 'true')}>
                      <FormControlLabel value="true" control={<Radio />} label="Yes — 12+ hours notice" />
                      <FormControlLabel value="false" control={<Radio />} label="No — less than 12 hours" />
                    </RadioGroup>
                  </FormControl>

                  {advanceNoticeGiven === false && (
                    <Alert severity="error" sx={{ mt: 1 }}>⚠️ A charge will apply — the agency was not given 12 hours notice.</Alert>
                  )}
                  {advanceNoticeGiven === true && (
                    <Alert severity="success" sx={{ mt: 1 }}>No charge applies — sufficient notice was given.</Alert>
                  )}
                </Box>
              )}
              {cancelledByType === 'Agency' && (
                <Alert severity="info" sx={{ mt: 2 }}>No charge applies when the agency initiates the cancellation.</Alert>
              )}
            </Box>
          )}

          {!shiftToRelease?.isAgency && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2, fontStyle: 'italic' }}>This will make the shift available for assignment again.</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
          <Button onClick={handleCancelRelease}>Cancel</Button>
          <Button
            onClick={handleReleaseShift}
            variant="contained"
            color="warning"
            startIcon={<Delete />}
            disabled={shiftToRelease?.isAgency && (!cancelledByType || (cancelledByType === 'Beaumont' && advanceNoticeGiven === null))}
          >
            {shiftToRelease?.isAgency ? 'Mark as Cancelled' : 'Unassign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Post-cancellation notification dialog */}
      <Dialog open={cancelEmailDialogOpen} onClose={() => setCancelEmailDialogOpen(false)} maxWidth="sm" fullWidth className="no-print">
        <DialogTitle>Notify a Staff Member of the Cancellation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Optionally email a manager or admin to let them know this agency shift has been cancelled.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Staff Member</InputLabel>
            <Select value={cancelEmailRecipient} label="Staff Member" onChange={(e) => setCancelEmailRecipient(e.target.value)}>
              <MenuItem value="">Select...</MenuItem>
              {notifiableStaff.map((u) => (
                <MenuItem key={u._id} value={u._id}>{u.name} {u.isAdmin ? '(Admin)' : u.isManager ? '(Manager)' : ''}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelEmailDialogOpen(false)}>Skip</Button>
          <Button
            variant="contained"
            startIcon={<Email />}
            disabled={!cancelEmailRecipient}
            onClick={async () => {
              try {
                await notifyShiftCancellation({ shiftId: cancelEmailShiftId, recipientUserId: cancelEmailRecipient }).unwrap();
                showSnackbar('Cancellation notice sent', 'success');
                setCancelEmailDialogOpen(false);
              } catch (err) {
                showSnackbar(err?.data?.message || 'Failed to send notification', 'error');
              }
            }}
          >
            Send Notification
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Edit Shift Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCancelEdit} maxWidth="lg" fullWidth className="no-print">
        <DialogTitle>Edit Shift Details</DialogTitle>
        <DialogContent>
          {shiftToEdit && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>Editing shift in: <strong>{shiftToEdit.ward}</strong></Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>{shiftToEdit.isAgency ? 'Agency Worker:' : 'Staff:'} <strong>{shiftToEdit.staffName}</strong></Typography>

              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 2 }}>
                  <Tab label="Basic Information" value="basic" />
                  <Tab label="Schedule" value="schedule" />
                </Tabs>
              </Box>

              {activeTab === 'basic' && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Hospital Location</InputLabel>
                      <Select name="hospitalLocationId" value={editForm.hospitalLocationId || ''} label="Hospital Location" onChange={handleEditFormChange} size="small">
                        {filteredHospitalLocations?.map(location => (
                          <MenuItem key={location._id} value={location._id}>{location.wardDepartmentArea}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Category of Staff</InputLabel>
                      <Select name="categoryOfStaff" value={editForm.categoryOfStaff || ''} label="Category of Staff" onChange={handleEditFormChange} size="small">
                        <MenuItem value="">Select Category...</MenuItem>
                        {CATEGORY_OF_STAFF.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Reason for Request</InputLabel>
                      <Select name="reasonForRequest" value={editForm.reasonForRequest || ''} label="Reason for Request" onChange={handleEditFormChange} size="small">
                        <MenuItem value="">Select Reason...</MenuItem>
                        {REASON_FOR_REQUEST.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>

                  {['1-1 special', 'Cohorts'].some(r => editForm.reasonForRequest?.toLowerCase() === r.toLowerCase()) && (
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth margin="normal" required>
                        <InputLabel>Specials / Cohorts Reason</InputLabel>
                        <Select name="specialsReason" value={editForm.specialsReason || ''} label="Specials / Cohorts Reason" onChange={handleEditFormChange} size="small">
                          <MenuItem value="">Select reason...</MenuItem>
                          {SPECIALS_REASON.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <TextField fullWidth label="Reason for Request Details" name="reasonForRequestDetails" value={editForm.reasonForRequestDetails || ''} onChange={handleEditFormChange} margin="normal" multiline rows={3} placeholder="Provide details about why this shift is needed..." />
                  </Grid>

                  {shiftToEdit?.isAgency && (
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: '#faf5ff' }}>
                        <Typography variant="subtitle2" gutterBottom>Agency Worker Details</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                          Update the worker's name or contact details if they've changed since assignment.
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="Worker Name" name="workerName" value={editForm.workerName || ''} onChange={handleEditFormChange} />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="Contact Number" name="workerContact" value={editForm.workerContact || ''} onChange={handleEditFormChange} />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="Contact Email" name="workerEmail" type="email" value={editForm.workerEmail || ''} onChange={handleEditFormChange} />
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8f9fa' }}>
                      <Typography variant="subtitle2" gutterBottom>Options</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={4}>
                          <FormControlLabel control={<Checkbox checked={editForm.escort || false} onChange={(e) => setEditForm(prev => ({ ...prev, escort: e.target.checked }))} name="escort" />} label="Escort Required" />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <FormControlLabel control={<Checkbox checked={editForm.special || false} onChange={(e) => setEditForm(prev => ({ ...prev, special: e.target.checked }))} name="special" />} label="Is this a Special" />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <FormControlLabel control={<Checkbox checked={editForm.isOvertime || false} onChange={(e) => setEditForm(prev => ({ ...prev, isOvertime: e.target.checked }))} name="isOvertime" />} label="Overtime Shift" />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={editForm.isActive !== false}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  if (!checked && shiftToEdit?.isAgency) {
                                    const shiftObj = wardShifts[shiftToEdit.ward]?.[shiftToEdit.dayDate]?.find(s => s.id === shiftToEdit.shiftId);
                                    setEditDialogOpen(false);
                                    if (shiftObj) openCancelDialogForShift(shiftObj);
                                    return;
                                  }
                                  setEditForm(prev => ({ ...prev, isActive: checked }));
                                }}
                                name="isActive"
                              />
                            }
                            label="Active Shift"
                          />
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', ml: 4 }}>Uncheck to "delete" the shift (sets isActive=false)</Typography>
                        </Grid>
                        <Grid item xs={12} sm={8}>
                          <TextField fullWidth label="ECC Approval Number" name="eccNumber" value={editForm.eccNumber || ''} onChange={handleEditFormChange} size="small" placeholder="Enter ECC number..." />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Special Requirements" name="specialRequirements" value={editForm.specialRequirements || ''} onChange={handleEditFormChange} margin="normal" multiline rows={4} placeholder="Any special equipment, training, or requirements..." />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Comments" name="comments" value={editForm.comments || ''} onChange={handleEditFormChange} margin="normal" multiline rows={4} placeholder="Additional notes or comments..." />
                  </Grid>
                </Grid>
              )}

              {activeTab === 'schedule' && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Shift Time Preset</Typography>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Grid container spacing={1}>
                        {Object.entries(SHIFT_PRESETS).map(([key, preset]) => (
                          <Grid item xs={6} md={4} key={key}>
                            <Paper elevation={editForm.selectedPreset === key ? 2 : 0} sx={{ p: 1, cursor: 'pointer', bgcolor: editForm.selectedPreset === key ? 'primary.main' : 'grey.100', color: editForm.selectedPreset === key ? 'white' : 'inherit', '&:hover': { bgcolor: editForm.selectedPreset === key ? 'primary.dark' : 'grey.200' } }} onClick={() => handlePresetSelect(key)}>
                              <Typography variant="body2">{preset.label}</Typography>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </Paper>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <TextField fullWidth label="Start Date" type="date" name="startDate" value={editForm.startDate || ''} onChange={handleEditFormChange} InputLabelProps={{ shrink: true }} margin="normal" size="small" />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField fullWidth label="End Date (Same Day)" type="date" name="endDate" value={editForm.endDate || ''} onChange={handleEditFormChange} InputLabelProps={{ shrink: true }} margin="normal" size="small" helperText="Same day unless overnight shift" />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField fullWidth label="Start Time" type="time" name="startTime" value={editForm.startTime || ''} onChange={handleEditFormChange} InputLabelProps={{ shrink: true }} margin="normal" size="small" />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField fullWidth label="End Time" type="time" name="endTime" value={editForm.endTime || ''} onChange={handleEditFormChange} InputLabelProps={{ shrink: true }} margin="normal" size="small" />
                  </Grid>

                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ mt: 1 }}>
                      <Typography variant="body2"><strong>Shift Duration:</strong> {calculateShiftDuration()}</Typography>
                    </Alert>
                  </Grid>
                </Grid>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit}>Cancel</Button>
          <Button onClick={handleUpdateShift} variant="contained" color="primary" startIcon={<Edit />}>Update Shift</Button>
        </DialogActions>
      </Dialog>

      {/* Email Dialog (for web email fallback) */}
      <Dialog open={emailDialogOpen} onClose={handleCancelEmail} maxWidth="md" fullWidth className="no-print">
        <DialogTitle>Email Ward Schedule</DialogTitle>
        <DialogContent>
          {selectedWardForEmail && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body1" gutterBottom><strong>Ward:</strong> {selectedWardForEmail.name}</Typography>
                <Typography variant="body2"><strong>Week:</strong> {formatDateRange()}</Typography>
                <Typography variant="body2"><strong>Generated By:</strong> {userInfo?.name || 'Manager'}</Typography>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Email Method:</Typography>
                  <ToggleButtonGroup value={emailMethod} exclusive onChange={(e, newMethod) => newMethod && setEmailMethod(newMethod)} size="small">
                    <ToggleButton value="outlook"><DesktopWindows sx={{ mr: 1 }} />Outlook</ToggleButton>
                    <ToggleButton value="web"><Language sx={{ mr: 1 }} />Web Email</ToggleButton>
                  </ToggleButtonGroup>

                  <FormControlLabel control={<Switch checked={includeWeekend} onChange={(e) => setIncludeWeekend(e.target.checked)} size="small" />} label="Include Weekend Days" sx={{ mt: 1, display: 'block' }} />
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                    {includeWeekend ? 'Saturday and Sunday will be included' : 'Only Monday to Friday will be included'}
                  </Typography>
                </Box>
              </Box>

              {emailMethod === 'web' && (
                <>
                  <TextField fullWidth label="Email Subject" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} margin="normal" />
                  <TextField fullWidth label="Additional Notes (optional)" value={emailContent} onChange={(e) => setEmailContent(e.target.value)} margin="normal" multiline rows={4} helperText="Add any additional notes or instructions for the ward" />
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>Schedule Preview (showing {includeWeekend ? 'all 7 days' : '5 weekdays'}):</Typography>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: '#f9f9f9', maxHeight: '200px', overflow: 'auto' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {generateWardSchedulePlainText(selectedWardForEmail.name).slice(0, 500)}...
                    </Typography>
                  </Paper>
                </>
              )}

              {emailMethod === 'outlook' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Clicking "Send Email" will open Microsoft Outlook with the ward schedule pre-populated.
                    If Outlook is not installed, the system will automatically fall back to web email.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                    Weekend days are {includeWeekend ? 'INCLUDED' : 'NOT INCLUDED'} in this email.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEmail}>Cancel</Button>
          <Button onClick={handleSendEmail} variant="contained" color="primary" startIcon={emailMethod === 'outlook' ? <DesktopWindows /> : <Send />}>
            {emailMethod === 'outlook' ? 'Open in Outlook' : 'Send Web Email'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        className="no-print"
      />
    </Box>
  );
};

export default WardWeeklyView;
